import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Box, Heading, VStack, FormControl, FormLabel, Select, Button, Card, CardBody, Text, useColorModeValue, HStack, Flex, Input, useToast, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, Table, Thead, Tbody, Tr, Th, Td, NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper, List, ListItem, Tag, TagLabel, TagCloseButton, Collapse, Textarea, Switch, Checkbox, Divider
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigation } from '../contexts/NavigationContext';
import SearchableSelect from '../components/SearchableSelect';
import { FiX, FiPlus, FiDollarSign, FiTrash2 } from 'react-icons/fi';
import FormulaCalculator from '../services/formulaCalculator';
import { useCurrency } from '../contexts/CurrencyContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableSection from '../components/proposal/SortableSection';
import SortableItem from '../components/proposal/SortableItem';

// Inline field: label + control in a single row
const InlineField = ({ label, required = false, children, minLabelW = '140px' }) => (
  <HStack align="center" spacing={4} w="100%">
    <Box minW={minLabelW} w={minLabelW}>
      <Text fontWeight="semibold" textAlign="left">
        {label} {required ? '*' : ''}
      </Text>
    </Box>
    <Box flex={1}>{children}</Box>
  </HStack>
);

const ProposalEdit = () => {
  const { id } = useParams();
  const { t, currentLanguage } = useLanguage();
  const { registerUnsavedChanges, clearUnsavedChanges } = useNavigation();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const { data: proposal, isLoading } = useQuery(['proposal', id], () => api.get(`/api/proposals/${id}`).then(res => res.data.data.proposal));
  const { data: templates } = useQuery('proposal_templates', () => api.get('/api/settings/templates').then(res => res.data.data.templates));
  const { data: sections } = useQuery('sections', () => api.get('/api/meta/sections').then(res => res.data.data.sections));
  const { data: units } = useQuery('units', () => api.get('/api/meta/units').then(res => res.data.data.units));

  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [sectionBlocks, setSectionBlocks] = useState([]); // [{section_id, items: [{id,name,quantity,unit_price}]}]

  const [selection, setSelection] = useState({ client_id: '', client_name: '', client_company: '', template_name: '', template_id: '' });
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('1');
  const [metaOpen, setMetaOpen] = useState(false);
  const [footerOpen, setFooterOpen] = useState(false);
  const FooterEditor = ({ value, onChange }) => {
    const ref = useRef(null);
    const [html, setHtml] = useState(value || '');
    
    // value prop değiştiğinde html state'ini güncelle
    useEffect(() => {
      setHtml(value || '');
    }, [value]);
    const [isFocused, setIsFocused] = useState(false);
    const sanitizeClient = (html) => {
      if (!html) return '';
      
      let s = String(html);
      
      // Sadece metin varsa olduğu gibi döndür
      if (!/<[^>]*>/.test(s)) {
        return s;
      }
      
      // Basit HTML temizleme
      s = s.replace(/<\s*strong\s*>/gi, '<b>').replace(/<\s*\/\s*strong\s*>/gi, '</b>');
      s = s.replace(/<\s*span[^>]*font-weight\s*:\s*(bold|700|800|900)[^>]*>([\s\S]*?)<\s*\/\s*span\s*>/gi, '<b>$2</b>');
      s = s.replace(/<\s*(div|p)\s*>/gi, '').replace(/<\s*\/\s*(div|p)\s*>/gi, '<br>');
      s = s.replace(/<(?!\/?(b|br|ul|li)\b)[^>]*>/gi, '');
      s = s.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
      
      return s;
    };
    useEffect(() => {
      // Sadece editör focus değilken ve değer değiştiğinde DOM'u güncelle
      if (!isFocused && ref.current && ref.current.innerHTML !== (value || '')) {
        ref.current.innerHTML = value || '';
      }
    }, [value, isFocused, html]);
    const applyBold = () => {
      try {
        if (!ref.current) return;
        
        // Editörü focus yap
        ref.current.focus({ preventScroll: true });
        
        // Bold komutunu uygula
        document.execCommand('bold', false, null);
        
        // İçeriği güncelle
        const currentContent = ref.current.innerHTML;
        setHtml(currentContent);
      } catch (error) {
        console.warn('Bold apply error:', error);
      }
    };
    const onPaste = (e) => {
      try {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        const html = (text || '').replace(/\r?\n/g, '<br>');
        document.execCommand('insertHTML', false, sanitizeClient(html));
        setHtml(sanitizeClient(ref.current.innerHTML));
      } catch(_){}
    };
    const onKeyDown = () => {};
    return (
      <VStack align="stretch" spacing={2}>
        <HStack>
          <Button size="xs" onMouseDown={(e)=> e.preventDefault()} onClick={applyBold} fontWeight="bold">B</Button>
        </HStack>
        <Box
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={(e)=> { 
            const raw = e.currentTarget.innerHTML; 
            setHtml(raw);
            // onInput sırasında onChange çağırma - sadece onBlur'da çağır
          }}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          onFocus={()=> setIsFocused(true)}
          onBlur={()=> { 
            setIsFocused(false); 
            // Mevcut içeriği al ve temizle
            const currentContent = ref.current ? ref.current.innerHTML : html;
            const sanitized = sanitizeClient(currentContent);
            setHtml(sanitized);
            onChange(sanitized);
          }}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          minH="140px"
          p={3}
          bg="white"
          fontSize="14px"
          lineHeight="1.4"
          _focusWithin={{ outline: 'none', boxShadow: '0 0 0 1px #3182ce' }}
        />
      </VStack>
    );
  };
  const [meta, setMeta] = useState({ project: '', subject: '', offer_status: '', in_charge: [], notes: '', footer_text: '', footer_signature_left: '', footer_signature_right: '' });
  const [proposalType, setProposalType] = useState('offer'); // 'offer' or 'proforma'
  const [showDetailedPricing, setShowDetailedPricing] = useState(true);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [overrideTotal, setOverrideTotal] = useState('');
  const { data: userNames } = useQuery('user_names', () => api.get('/api/users/names').then(res => res.data.data.users));

  const [editingUnits, setEditingUnits] = useState({}); // {sectionIndex_itemIndex: true}
  const [editingUsage, setEditingUsage] = useState(null); // Currently editing usage cell
  const [templateUsages, setTemplateUsages] = useState({}); // Template'den gelen usages
  const [itemUsages, setItemUsages] = useState({}); // {sectionIndex_itemIndex_usageKey: {ratio: 1.05}}
  const [formulaRules, setFormulaRules] = useState([]); // Template'e ait formula rules
  const [itemCalculations, setItemCalculations] = useState({}); // Hesaplama sonuçları
  const [rowContextMenu, setRowContextMenu] = useState({ open: false, x: 0, y: 0, sectionIndex: null, itemIndex: null }); // Context menu for rows
  const [clientPreviewOpen, setClientPreviewOpen] = useState(false);
  const [versionNote, setVersionNote] = useState('');
  const [clientPreview, setClientPreview] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [inventoryNameCache, setInventoryNameCache] = useState({}); // {id: {name_en, name_de}}
  const [serviceNameCache, setServiceNameCache] = useState({}); // {id: {name_en, name_de}}
  const [navigationRegistered, setNavigationRegistered] = useState(false);
  // Create item modal state
  const [createItemModal, setCreateItemModal] = useState({ isOpen: false, label: '', sectionIndex: null });
  
  // Contractor modal states
  const [contractorModal, setContractorModal] = useState({ isOpen: false, sectionIndex: null, itemIndex: null });
  const [contractorItems, setContractorItems] = useState([]); // [{name, quantity, unit_price, total_price}]
  const [selectedContractor, setSelectedContractor] = useState('');
  const [contractorSearch, setContractorSearch] = useState('');
  const [contractorSuggestions, setContractorSuggestions] = useState([]);
  const [showContractorSuggestions, setShowContractorSuggestions] = useState(false);
  
  // New supplier modal state
  const [newSupplierModal, setNewSupplierModal] = useState(false);
  const [newSupplierForm, setNewSupplierForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    category: '',
    rating: '',
    notes: ''
  });
  
  // Handle exit with confirmation
  const handleExit = (targetPath = '/proposals') => {
    if (hasUnsavedChanges) {
      setPendingNavigation(targetPath);
      setShowExitConfirm(true);
    } else {
      navigate(targetPath);
    }
  };

  const confirmExit = () => {
    setShowExitConfirm(false);
    setNavigationRegistered(false);
    clearUnsavedChanges();
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const cancelExit = () => {
    setShowExitConfirm(false);
    setPendingNavigation(null);
  };

  // Invoicing modals state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceCreateOpen, setInvoiceCreateOpen] = useState(false);
  const [invoiceCreateForm, setInvoiceCreateForm] = useState({
    invoice_type: 'advance',
    mode: 'percent',
    value: 50,
    due_date: '',
    notes: '',
    items: [] // for extra
  });
  
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { data: invoiceSummary, refetch: refetchInvoiceSummary } = useQuery(['proposal_invoices', id], () => api.get(`/api/proposals/${id}/invoices`).then(res => res.data.data), { enabled: invoiceModalOpen || invoiceCreateOpen });
  const [addPaymentState, setAddPaymentState] = useState({}); // {invoiceId: {amount, payment_date, method}}

  // Formula Calculator instance
  const formulaCalculator = new FormulaCalculator();

  // Load proposal data into form when proposal is fetched
  useEffect(() => {
    if (proposal) {
 
      setSelection({
        client_id: proposal.client_id || '',
        client_name: proposal.client_name || '',
        client_company: proposal.client_company || '',
        template_id: proposal.template_id || '',
        template_name: ''
      });
      setTitle(proposal.title || '');
      setVersion(proposal.version_number?.toString() || '1');
      setVersionNote(proposal.version_note || '');
      
      // Load proposal type and pricing settings
      setProposalType(proposal.proposal_type === 'proforma' ? 'proforma' : 'offer');
      setShowDetailedPricing(proposal.show_detailed_pricing === 1 || proposal.show_detailed_pricing === true || proposal.show_detailed_pricing === undefined || proposal.show_detailed_pricing === null);

      // Load override settings
      setOverrideEnabled(proposal.override_enabled === 1 || proposal.override_enabled === true);
      setOverrideTotal(proposal.override_total !== null && proposal.override_total !== undefined ? String(proposal.override_total) : '');

      // Load meta data
      setMeta({
        project: proposal.project || '',
        subject: proposal.description || '',
        offer_status: proposal.offer_status || '',
        in_charge: proposal.in_charge ? (typeof proposal.in_charge === 'string' ? (proposal.in_charge.startsWith('[') ? JSON.parse(proposal.in_charge) : proposal.in_charge.split(',').filter(Boolean)) : Array.isArray(proposal.in_charge) ? proposal.in_charge : []) : [],
        notes: proposal.notes || '',
        footer_text: proposal.footer_text || '',
        footer_signature_left: proposal.footer_signature_left || '',
      });
    }
  }, [proposal]);

  // Track changes to detect unsaved modifications
  useEffect(() => {
    if (proposal) {
      const hasChanges = 
        selection.client_id !== (proposal.client_id || '') ||
        selection.template_id !== (proposal.template_id || '') ||
        title !== (proposal.title || '') ||
        version !== (proposal.version_number?.toString() || '1') ||
        versionNote !== (proposal.version_note || '') ||
        meta.project !== (proposal.project || '') ||
        meta.subject !== (proposal.description || '') ||
        meta.offer_status !== (proposal.offer_status || '') ||
        JSON.stringify(meta.in_charge || []) !== JSON.stringify(proposal.in_charge ? (typeof proposal.in_charge === 'string' ? (proposal.in_charge.startsWith('[') ? JSON.parse(proposal.in_charge) : proposal.in_charge.split(',').filter(Boolean)) : Array.isArray(proposal.in_charge) ? proposal.in_charge : []) : []) ||
        meta.notes !== (proposal.notes || '') ||
        meta.footer_text !== (proposal.footer_text || '') ||
        meta.footer_signature_left !== (proposal.footer_signature_left || '') ||
        meta.footer_signature_right !== (proposal.footer_signature_right || '');
      
      setHasUnsavedChanges(hasChanges);
      
      // Register with navigation context
      if (hasChanges && !navigationRegistered) {
        setNavigationRegistered(true);
        registerUnsavedChanges((navigate, targetPath) => {
          setPendingNavigation(targetPath);
          setTimeout(() => {
            setShowExitConfirm(true);
          }, 0);
        });
      } else if (!hasChanges && navigationRegistered) {
        setNavigationRegistered(false);
        clearUnsavedChanges();
      }
    }
  }, [proposal, selection, title, version, versionNote, meta, registerUnsavedChanges, clearUnsavedChanges]);

  // Add a history state when component mounts to detect back navigation
  useEffect(() => {
    window.history.pushState({ hasUnsavedChanges: false }, '', window.location.href);
  }, []);

  // Handle browser back/close with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = t('proposals.unsaved_changes_warning', {defaultValue: 'You have unsaved changes. Are you sure you want to leave?'});
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        setPendingNavigation('/proposals');
        setShowExitConfirm(true);
        // Push the current state back to prevent navigation
        window.history.pushState({ hasUnsavedChanges: false }, '', window.location.href);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, t]);

  // Calculate values using formula engine (copied from create)
  const calculateItemValues = async (item, sectionMargin, sectionIndex, itemIndex) => {
    // yüzde olarak tutulan margin'i 1.xx'e dönüştürerek hesapla
    // item.margin ve sectionMargin UI'de yüzde formatında (40 gibi)
    // Her zaman yüzde olarak kabul et ve 1 + (margin/100) şeklinde dönüştür
    const marginValue = Number((item.margin === 0 || item.margin === null || item.margin === undefined)
      ? ( (sectionMargin === 0 || sectionMargin === null || sectionMargin === undefined) ? 1.0 : (1 + (sectionMargin/100)) )
      : (1 + (item.margin/100)));
    
    const variables = {
      qty: Number(item.quantity || 0),
      unit_price: Number(item.unit_price || 0),
      margin: marginValue,
      ...Object.entries(templateUsages).reduce((acc, [usageKey, usage]) => {
        const normalizedKey = usageKey.replace('usage_', 'usage');
        const itemUsageKey = `${sectionIndex}_${itemIndex}_${usageKey}`;
        const customRatio = itemUsages[itemUsageKey]?.ratio;
        acc[`${normalizedKey}_ratio`] = customRatio !== undefined ? Number(customRatio) : Number(usage.ratio || 1);
        return acc;
      }, {})
    };

    formulaCalculator.setVariables(variables);

    let formulas = formulaRules;
    if (formulas.length === 0) {
      formulas = await loadDefaultTemplateFormulas();
    }
    
    const results = formulaCalculator.executeFormulas(formulas);
    
    Object.entries(templateUsages).forEach(([usageKey]) => {
      const normalizedKey = usageKey.replace('usage_', 'usage');
      const usageResult = results[`template.${normalizedKey}.rowResult`];
      if (usageResult !== undefined) {
        variables[`${normalizedKey}_rowResult`] = usageResult;
      }
    });
    
    if (Object.keys(templateUsages).length > 0) {
      formulaCalculator.setVariables(variables);
      const updatedResults = formulaCalculator.executeFormulas(formulas);
      Object.assign(results, updatedResults);
    }
    
    // SPPU'yu 2 ondalık basamağa yuvarla (tutarlılık için)
    const rawSppu = results['table.sppu'] || 0;
    const sppu = Math.round(rawSppu * 100) / 100;

    // SLT'yi yuvarlanmış SPPU ile hesapla (25.00 × 72 = 1800.00, 25.004 × 72 = 1800.29 değil)
    const qty = Number(variables.qty || 0);
    const slt = Math.round((qty * sppu) * 100) / 100;

    // unit_price_x_amount ve final_price yuvarlanmış değerlerle hesapla (tutarlılık için)
    const unit_price = Number(variables.unit_price || 0);
    const unit_price_x_amount = Math.round((qty * unit_price) * 100) / 100;
    const final_price = slt; // final_price = slt (zaten yuvarlanmış)

    return {
      sppu,
      slt,
      final_price,
      unit_price_x_amount,
      usages: Object.entries(templateUsages).reduce((acc, [usageKey, usage]) => {
        // Her zaman yuvarlanmış SLT üzerinden hesapla (tutarlılık için)
        // Formula engine ham değerlerle hesaplıyor, bu yüzden onun sonucunu kullanmıyoruz
        const ratio = Number(usage.ratio || 1);
        acc[usageKey] = Math.round((slt * ratio) * 100) / 100;
        return acc;
      }, {})
    };
  };

  // Load formula rules for template
  const loadFormulaRules = async (templateId) => {
    try {
      const response = await api.get(`/api/formula-rules/template/${templateId}`);
      setFormulaRules(response.data.data.rules || []);
    } catch (error) {
      console.warn('Failed to load formula rules:', error);
      setFormulaRules([]);
    }
  };
  const loadDefaultTemplateFormulas = async () => {
    try {
      const response = await api.get('/api/formula-rules/template/1');
      return response.data.data.rules || [];
    } catch (error) {
      console.warn('Failed to load default template formulas:', error);
      return [];
    }
  };

  // Initialize from existing proposal
  useEffect(() => {
    if (!proposal) return;
 
    setTitle(proposal.title || '');
    setVersion(String(proposal.version_number || '1'));
    setSelection(prev => ({ ...prev, client_id: proposal.client_id || '', client_name: proposal.client_name || '', client_company: proposal.client_company || '' }));
    setMeta({ 
      project: proposal.project || '', 
      subject: proposal.description || '', 
      offer_status: proposal.offer_status || '', 
      in_charge: proposal.in_charge ? (typeof proposal.in_charge === 'string' ? (proposal.in_charge.startsWith('[') ? JSON.parse(proposal.in_charge) : proposal.in_charge.split(',').filter(Boolean)) : Array.isArray(proposal.in_charge) ? proposal.in_charge : []) : [], 
      notes: proposal.notes || '',
      footer_text: proposal.footer_text || '',
    });

    if (proposal.template_id) {
      setSelection(prev => ({ ...prev, template_id: proposal.template_id, template_name: '' }));
      if (proposal.template_json) {
        try {
          const templateData = typeof proposal.template_json === 'string' ? JSON.parse(proposal.template_json) : proposal.template_json;
          setTemplateUsages(templateData.usages || {});
          loadFormulaRules(proposal.template_id);
        } catch (_) {
          setTemplateUsages({});
        }
      } else {
        setTemplateUsages({});
      }
    } else {
      setSelection(prev => ({ ...prev, template_id: '', template_name: '' }));
      setTemplateUsages({});
    }

    if (Array.isArray(proposal.sections)) {
      const blocks = proposal.sections.map(section => {
        // Get translations from sections table if available
        const sec = sections?.find(s => s.id === section.section_id);
        const nameEn = sec?.name_en || section.name || 'Section';
        const nameDe = sec?.name_de || section.name || 'Section';
        const sectionName = currentLanguage === 'de' ? nameDe : nameEn;
        
        return {
          section_id: section.section_id,
          section_name: sectionName,
          name_en: nameEn,
          name_de: nameDe,
          // DB'den 1.4 geliyorsa, UI'de 40 olarak göster (string olarak gelebiliyor, number'a çevir)
          // CRITICAL: Parse section margin correctly - DB always stores as 1.xx format
          margin: (() => {
            if (!section.margin) return 0;
            const marginVal = typeof section.margin === 'string' ? parseFloat(section.margin) : Number(section.margin);
            if (isNaN(marginVal)) {
              console.warn('⚠️ Invalid section margin:', section.margin);
              return 0;
            }
            // DB always stores margin as 1.xx format (e.g., 1.4 = 40%)
            // Convert to percentage for UI
            if (marginVal >= 1 && marginVal < 2) {
              return (marginVal - 1) * 100;
            } else if (marginVal >= 100) {
              // Edge case: already in percentage but > 100, log warning
              console.warn('⚠️ Section margin already in percentage format (>100):', marginVal);
              return marginVal;
            } else {
              // Edge case: already in percentage format (< 100)
              console.warn('⚠️ Section margin already in percentage format:', marginVal);
              return marginVal;
            }
          })(),
        items: (section.items || []).map(item => {
          // json_data'yı önce parse et
          const jsonData = item.json_data && typeof item.json_data === 'string' ? JSON.parse(item.json_data) : item.json_data || {};
          
          // margin artık sadece json_data içinde, oradan al ve yüzdeye çevir
          // CRITICAL: Parse margin values correctly - ensure proper number conversion
          const jsonMarginNum = (jsonData.margin !== null && jsonData.margin !== undefined) 
            ? (typeof jsonData.margin === 'string' ? parseFloat(jsonData.margin) : Number(jsonData.margin))
            : null;
          const sectionMarginNum = (section.margin !== null && section.margin !== undefined)
            ? (typeof section.margin === 'string' ? parseFloat(section.margin) : Number(section.margin))
            : null;
          
          // margin'i yüzdeye çevir (1.4 → 40, 1.5 → 50) - UI için
          // Eğer margin >= 1 ise 1.xx formatında, < 1 ise zaten yüzde formatında
          let marginPercent = 0;
          if (jsonMarginNum !== null && !isNaN(jsonMarginNum)) {
            // If margin is already in percentage format (< 1 or > 100), use as is
            // If margin is in decimal format (>= 1 and < 2), convert to percentage
            if (jsonMarginNum >= 1 && jsonMarginNum < 2) {
              marginPercent = (jsonMarginNum - 1) * 100;
            } else if (jsonMarginNum >= 100) {
              // Already in percentage format but > 100, use as is
              marginPercent = jsonMarginNum;
            } else {
              // Already in percentage format (< 100)
              marginPercent = jsonMarginNum;
            }
          } else if (sectionMarginNum !== null && !isNaN(sectionMarginNum)) {
            if (sectionMarginNum >= 1 && sectionMarginNum < 2) {
              marginPercent = (sectionMarginNum - 1) * 100;
            } else if (sectionMarginNum >= 100) {
              marginPercent = sectionMarginNum;
            } else {
              marginPercent = sectionMarginNum;
            }
          }
          // Yuvarlama hatası düzeltmesi: 39.99999 gibi değerleri 40'a yuvarla
          marginPercent = Math.round(marginPercent * 100) / 100;
          
          // Debug log for suspicious margin values
          if (marginPercent > 1000 || isNaN(marginPercent)) {
            console.warn('⚠️ Suspicious margin value:', {
              item_id: item.item_id,
              jsonMarginNum,
              sectionMarginNum,
              marginPercent,
              raw_json_margin: jsonData.margin,
              raw_section_margin: section.margin
            });
          }
          
          // Units: önce json_data'dan, sonra backend'den gelen dil bazlı değerlerden, son olarak item.uom_abbr'den al
          const uomAbbr = jsonData.uom_abbr 
            || (currentLanguage === 'de' ? (item.uom_abbr_de || item.uom_abbr) : (item.uom_abbr_en || item.uom_abbr))
            || item.uom_abbr
            || null;
          
          // Get name from jsonData (which now contains updated inventory names from backend)
          // Fallback to current language preference
          const itemName = jsonData.name_en || jsonData.name_de || item.name || '';
          
          // CRITICAL: Parse quantity and unit_price from DB as numbers
          // Ensure we never use calculated values, only original DB values
          const dbQuantity = (() => {
            const val = typeof item.quantity === 'string' ? parseFloat(item.quantity) : Number(item.quantity || 1);
            if (isNaN(val) || val > 1000000) {
              console.warn('⚠️ Suspicious quantity from DB:', { item_id: item.item_id, raw: item.quantity, parsed: val });
              return 1;
            }
            return val;
          })();
          const dbUnitPrice = (() => {
            const val = typeof item.unit_price === 'string' ? parseFloat(item.unit_price) : Number(item.unit_price || 0);
            if (isNaN(val) || val > 1000000) {
              console.warn('⚠️ Suspicious unit_price from DB:', { item_id: item.item_id, raw: item.unit_price, parsed: val });
              return 0;
            }
            return val;
          })();
          
          return {
            ...jsonData, // önce json_data'yı spread et (contains updated name_en and name_de from inventory)
            item_id: item.item_id,
            name: itemName, // Use name from jsonData (updated from inventory) or fallback
            uom_abbr: uomAbbr,
            quantity: dbQuantity,
            unit_price: dbUnitPrice,
            margin: marginPercent, // UI için yüzde değeri (json_data.margin'i override ediyor)
            contractor: item.contractor,
            contractor_items: [],
            contractor_total: null,
            notes: item.notes,
            optional: Boolean(item.optional),
            optional_hide_prices: Boolean(item.optional_hide_prices),
            hide_price_note: jsonData?.hide_price_note || null,
            product_note: jsonData?.product_note || null,
          };
        })
      };
      });
      setSectionBlocks(blocks);

      const usages = {};
      blocks.forEach((block, blockIdx) => {
        block.items.forEach((item, itemIdx) => {
          if (item.itemUsages) {
            Object.entries(item.itemUsages).forEach(([usageKey, ratio]) => {
              const key = `${blockIdx}_${itemIdx}_${usageKey}`;
              usages[key] = { ratio: Number(ratio) };
            });
          }
        });
      });
      setItemUsages(usages);
    }
  }, [proposal]);

  // Update section names when language changes
  useEffect(() => {
    if (sectionBlocks.length === 0) return;
    setSectionBlocks(prev => prev.map(block => {
      if (block.name_en || block.name_de) {
        const sectionName = currentLanguage === 'de' 
          ? (block.name_de || block.name_en || 'Section')
          : (block.name_en || block.name_de || 'Section');
        return { ...block, section_name: sectionName };
      }
      // If no translations available, try to get from sections table
      if (block.section_id && sections) {
        const sec = sections.find(s => s.id === block.section_id);
        if (sec) {
          const nameEn = sec.name_en || block.section_name || 'Section';
          const nameDe = sec.name_de || sec.name_en || block.section_name || 'Section';
          const sectionName = currentLanguage === 'de' ? nameDe : nameEn;
          return { ...block, section_name: sectionName, name_en: nameEn, name_de: nameDe };
        }
      }
      return block;
    }));
  }, [currentLanguage, sections]);

  // Ensure language-specific names for items in edit view (detect service vs product robustly)
  useEffect(() => {
    const loadMissingNames = async () => {
      try {
        const allMissing = new Set();
        const inventoryMissing = new Set();
        const serviceMissing = new Set();
        (sectionBlocks || []).forEach(sec => {
          (sec.items || []).forEach(it => {
            const effectiveType = it.item_type ? it.item_type : (it.item_id ? 'product' : 'service');
            const effectiveId = effectiveType === 'service' ? (it.service_id || it.item_id || it.id) : (it.item_id || it.id);
            if (effectiveId && (!it?.name_en || !it?.name_de)) {
              allMissing.add(effectiveId);
              if (effectiveType === 'service') serviceMissing.add(effectiveId); else inventoryMissing.add(effectiveId);
            }
          });
        });
        // First, try services for all missing ids (batch)
        if (allMissing.size > 0) {
          try {
            const res = await api.get('/api/services/names', { params: { ids: Array.from(allMissing).join(',') } });
            const list = res?.data?.data?.services || [];
            const map = {};
            const foundServiceIds = new Set();
            list.forEach(s => { map[s.id] = { name_en: s.name_en || '', name_de: s.name_de || '' }; foundServiceIds.add(Number(s.id)); });
            if (Object.keys(map).length > 0) setServiceNameCache(prev => ({ ...prev, ...map }));
            // Remaining → inventory
            const remaining = Array.from(inventoryMissing).filter(id => !foundServiceIds.has(Number(id)) && !inventoryNameCache[id]);
            for (const id of remaining) {
              try {
                const resInv = await api.get(`/api/inventory/${id}`);
                const item = resInv?.data?.data?.item;
                if (item) {
                  setInventoryNameCache(prev => ({ ...prev, [id]: { name_en: item.name_en || '', name_de: item.name_de || '' } }));
                }
              } catch (_) {}
            }
          } catch (_) {
            // Fallback: if services call fails, try inventory for all
            for (const id of allMissing) {
              try {
                const resInv = await api.get(`/api/inventory/${id}`);
                const item = resInv?.data?.data?.item;
                if (item) {
                  setInventoryNameCache(prev => ({ ...prev, [id]: { name_en: item.name_en || '', name_de: item.name_de || '' } }));
                }
              } catch (_) {}
            }
          }
        }
      } catch (_) {}
    };
    loadMissingNames();
  }, [sectionBlocks]);

  // Apply fetched names into sectionBlocks so UI can switch on currentLanguage; also fix item_type if determinable
  useEffect(() => {
    if (!sectionBlocks) return;
    setSectionBlocks(prev => prev.map(sec => ({
      ...sec,
      items: (sec.items || []).map(it => {
        if (!it?.item_id) return it;
        const svcKey = it.service_id || it.item_id;
        // If there is a service name for this id, force service typing and names
        if (svcKey && serviceNameCache[svcKey]) {
          const cache2 = serviceNameCache[svcKey];
          return { ...it, name_en: it.name_en || cache2.name_en || '', name_de: it.name_de || cache2.name_de || '', item_type: 'service', service_id: svcKey };
        }
        // Otherwise, if missing names and inventory has it, treat as product
        if ((!it?.name_en || !it?.name_de) && inventoryNameCache[it.item_id]) {
          const cache = inventoryNameCache[it.item_id];
          return { ...it, name_en: it.name_en || cache.name_en || '', name_de: it.name_de || cache.name_de || '', item_type: it.item_type || 'product' };
        }
        return it;
      })
    })));
  }, [inventoryNameCache, serviceNameCache]);

  // Re-apply formulas when rules/usages change - REMOVED to prevent double calculation
  // Calculations are now only done in itemCalculations useEffect and displayed from there
  // This prevents values from being multiplied when saving

  // Calculate all item values and store in state
  // Use ref to track previous input values and only recalculate when they actually change
  const prevInputValuesRef = useRef('');
  
  useEffect(() => {
    if (sectionBlocks.length === 0) return;
    
    // Create hash of ONLY input values (quantity, unit_price, margin) - ignore calculated values
    const currentInputHash = JSON.stringify(sectionBlocks.map(b => ({
      margin: b.margin,
      items: b.items.map(it => ({
        quantity: Number(it.quantity || 1),
        unit_price: Number(it.unit_price || 0),
        margin: it.margin || 0
      }))
    })));
    
    // Only recalculate if input values actually changed
    if (prevInputValuesRef.current === currentInputHash && Object.keys(itemCalculations).length > 0) return;
    prevInputValuesRef.current = currentInputHash;
    
    const calculateAllItems = async () => {
      const calculations = {};
      for (let sectionIndex = 0; sectionIndex < sectionBlocks.length; sectionIndex++) {
        const section = sectionBlocks[sectionIndex];
        for (let itemIndex = 0; itemIndex < section.items.length; itemIndex++) {
          const item = section.items[itemIndex];
          const key = `${sectionIndex}_${itemIndex}`;
          // CRITICAL: Only use base input values, NEVER use pre-calculated values
          // This prevents values from being multiplied on each save
          const baseItem = {
            quantity: Number(item.quantity || 1),
            unit_price: Number(item.unit_price || 0),
            margin: item.margin || 0,
            item_id: item.item_id,
            item_type: item.item_type,
            uom_abbr: item.uom_abbr
          };
          calculations[key] = await calculateItemValues(baseItem, section.margin, sectionIndex, itemIndex);
        }
      }
      setItemCalculations(calculations);
    };
    calculateAllItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionBlocks, formulaRules, templateUsages, itemUsages]);

  // Calculate average price per usage (live calculation)
  // Average = (SLT + usage_1 + usage_2 + ...) / (1 + number of usages)
  const averagePricePerUsage = useMemo(() => {
    if (sectionBlocks.length === 0) return 0;
    
    // Calculate total SLT
    const totalSLT = sectionBlocks.reduce((sum, section, sIdx) => {
      return sum + (section.items || []).reduce((sub, item, iIdx) => {
        if (item?.optional) return sub;
        const key = `${sIdx}_${iIdx}`;
        const calc = itemCalculations[key] || { slt: 0 };
        return sub + Number(calc.slt || 0);
      }, 0);
    }, 0);
    
    const deploymentCount = Object.keys(templateUsages).length;
    
    // If no usages, return just SLT
    if (deploymentCount === 0) {
      return totalSLT;
    }
    
    // Calculate usage totals
    const usageTotals = Object.keys(templateUsages).reduce((acc, usageKey) => {
      const total = sectionBlocks.reduce((sum, section, sIdx) => {
        return sum + (section.items || []).reduce((sub, item, iIdx) => {
          if (item?.optional) return sub;
          const key = `${sIdx}_${iIdx}`;
          const calc = itemCalculations[key] || { usages: {} };
          return sub + Number(calc.usages?.[usageKey] || 0);
        }, 0);
      }, 0);
      acc[usageKey] = total;
      return acc;
    }, {});

    const totalUsageSum = Object.values(usageTotals).reduce((a, b) => a + Number(b || 0), 0);
    
    // Average = (SLT + all usages) / (1 + number of usages)
    return (totalSLT + totalUsageSum) / (1 + deploymentCount);
  }, [sectionBlocks, itemCalculations, templateUsages]);

  // Section add
  const addSectionBlock = () => {
    if (!selectedSectionId) return;
    const sec = sections?.find(s => String(s.id) === String(selectedSectionId));
    if (!sec) return;
    setSectionBlocks(prev => [...prev, {
      section_id: sec.id, 
      section_name: sec.name_en || sec.name_de, 
      margin: 1.0,
      items: []
    }]);
    setSelectedSectionId('');
  };

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle section reorder
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    setSectionBlocks((sections) => {
      const oldIndex = sections.findIndex((_, i) => `section-${i}` === active.id);
      const newIndex = sections.findIndex((_, i) => `section-${i}` === over.id);
      return arrayMove(sections, oldIndex, newIndex);
    });
  };

  // Handle item reorder within a section
  const handleItemDragEnd = (sectionIndex) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    setSectionBlocks((sections) => {
      const section = sections[sectionIndex];
      const oldIndex = section.items.findIndex((_, i) => `item-${sectionIndex}-${i}` === active.id);
      const newIndex = section.items.findIndex((_, i) => `item-${sectionIndex}-${i}` === over.id);
      
      return sections.map((s, i) => 
        i === sectionIndex 
          ? { ...s, items: arrayMove(s.items, oldIndex, newIndex) }
          : s
      );
    });
  };

  // Contractor modal functions
  const openContractorModal = (sectionIndex, itemIndex) => {
    const item = sectionBlocks[sectionIndex]?.items[itemIndex];
    if (item?.contractor_items) {
      setContractorItems(item.contractor_items);
      setSelectedContractor(item.contractor || '');
      setContractorSearch(item.contractor || '');
    } else {
      setContractorItems([]);
      setSelectedContractor('');
      setContractorSearch('');
    }
    setContractorModal({ isOpen: true, sectionIndex, itemIndex });
  };

  const searchContractors = async (query) => {
    if (!query || query.length < 2) {
      setContractorSuggestions([]);
      setShowContractorSuggestions(false);
      return;
    }
    try {
      const response = await api.get(`/api/users/suppliers?q=${encodeURIComponent(query)}`);
      if (response.data.success) {
        setContractorSuggestions(response.data.data.suppliers || []);
        setShowContractorSuggestions(true);
      }
    } catch (error) {
      console.error('Error searching contractors:', error);
      setContractorSuggestions([]);
    }
  };

  const selectContractor = (contractor) => {
    setSelectedContractor({ id: contractor.id, name: contractor.name });
    setContractorSearch(contractor.name);
    setShowContractorSuggestions(false);
  };

  const addContractorItem = () => {
    setContractorItems(prev => [...prev, { name: '', quantity: 1, unit_price: 0, total_price: 0 }]);
  };

  const updateContractorItem = (index, field, value) => {
    setContractorItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? value : updated[index].quantity;
        const price = field === 'unit_price' ? value : updated[index].unit_price;
        updated[index].total_price = Number(qty) * Number(price);
      }
      return updated;
    });
  };

  const removeContractorItem = (index) => {
    setContractorItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveContractorData = () => {
    const { sectionIndex, itemIndex } = contractorModal;
    const totalAmount = contractorItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
    setSectionBlocks(prev => prev.map((section, i) => 
      i === sectionIndex ? {
        ...section,
        items: section.items.map((item, j) => 
          j === itemIndex ? {
            ...item,
            contractor_id: selectedContractor?.id || null,
            contractor: selectedContractor?.name || '',
            contractor_items: [...contractorItems],
            contractor_total: totalAmount,
            unit_price: (Number(item.quantity || 1) > 0) ? (Number(totalAmount) / Number(item.quantity || 1)) : 0
          } : item
        )
      } : section
    ));
    setContractorModal({ isOpen: false, sectionIndex: null, itemIndex: null });
    setContractorSearch('');
    setContractorSuggestions([]);
    setShowContractorSuggestions(false);
    toast({ title: t('common.success'), description: 'Contractor data saved successfully', status: 'success' });
  };

  // Save new supplier
  const saveNewSupplier = async () => {
    if (!newSupplierForm.name) {
      toast({ title: t('common.error'), description: 'Name is required', status: 'error' });
      return;
    }
    
    try {
      const response = await api.post('/api/users/suppliers', newSupplierForm);
      if (response.data.success) {
        // Backend returns {success: true, data: {id: X}}
        const supplierId = response.data.data.id;
        const newSupplier = {
          id: supplierId,
          name: newSupplierForm.name,
          email: newSupplierForm.email,
          phone: newSupplierForm.phone
        };
        // Set as selected contractor
        setSelectedContractor(newSupplier);
        setContractorSearch(newSupplier.name);
        // Close new supplier modal
        setNewSupplierModal(false);
        setNewSupplierForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          category: '',
          rating: '',
          notes: ''
        });
        toast({ title: t('common.success'), description: 'Supplier created successfully', status: 'success' });
      }
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast({ title: t('common.error'), description: error.response?.data?.error || 'Failed to create supplier', status: 'error' });
    }
  };

  // Update mutation
  const updateMutation = useMutation(
    (payload) => api.put(`/api/proposals/${id}`, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proposals');
        queryClient.invalidateQueries(['proposal', id]);
        toast({ title: t('common.success'), status: 'success' });
        try { localStorage.setItem('sidebar_collapsed','false'); window.dispatchEvent(new CustomEvent('sidebar:set-collapsed', { detail: false })); } catch {}
        navigate('/proposals');
      },
      onError: (error) => {
        toast({ title: t('common.error'), description: error.response?.data?.error || t('messages.server_error'), status: 'error' });
      }
    }
  );

  const handleSave = async () => {
    const sectionsPayload = sectionBlocks.map((b, sectionIdx) => {
      // Section margin: UI'de yüzde formatında (40 gibi), DB'ye 1.xx formatında kaydedilmeli
      let sectionMarginForDb = 1.0;
      if (b.margin !== null && b.margin !== undefined) {
        sectionMarginForDb = b.margin >= 1 ? b.margin : (1 + (b.margin / 100));
      }
      return {
        section_id: b.section_id || null,
        name: b.section_name,
        margin: sectionMarginForDb,
        order_index: sectionIdx, // Section sırası
        json_data: {
          margin: sectionMarginForDb,
          section_name: b.section_name
        },
        items: (b.items || []).map((it, idxItem) => {
          const calculationKey = `${sectionBlocks.indexOf(b)}_${idxItem}`;
          const calculations = itemCalculations[calculationKey] || {
            sppu: 0,
            slt: 0,
            final_price: 0,
            unit_price_x_amount: 0,
            usages: {}
          };
          // CRITICAL: Use ONLY original input values from sectionBlocks, never calculated values
          // This prevents values from being multiplied on each save
          const originalQuantity = Number(it.quantity || 1);
          const originalUnitPrice = Number(it.unit_price || 0);
          // Calculate line_total from original values
          const lineTotal = originalQuantity * originalUnitPrice;
          
          // Debug log to track values
          if (originalQuantity > 1000000 || originalUnitPrice > 1000000) {
            console.warn('⚠️ Suspicious values detected:', {
              item_id: it.item_id,
              quantity: originalQuantity,
              unit_price: originalUnitPrice,
              line_total: lineTotal
            });
          }
          
          return {
            item_id: it.item_id || null,
            name: it.name,
            item_type: it.item_type ? it.item_type : (it.item_id ? 'product' : 'service'),
            uom_abbr: it.uom_abbr || null,
            order_index: idxItem, // Item sırası
            contractor_id: it.contractor_id || null,
            contractor: it.contractor || null,
            contractor_items: Array.isArray(it.contractor_items) ? it.contractor_items : [],
            contractor_total: it.contractor_total !== undefined && it.contractor_total !== null ? Number(it.contractor_total) : null,
            final_price: calculations.final_price,
            unit_price_x_amount: calculations.unit_price_x_amount,
            notes: it.notes || null,
            optional: !!it.optional,
            optional_hide_prices: !!it.optional_hide_prices,
            description: null,
            quantity: originalQuantity,
            unit_price: originalUnitPrice,
            line_total: lineTotal,
            cost_type: 'per_use',
            sub_descriptions: Array.isArray(it.sub_descriptions) ? it.sub_descriptions : [],
            json_data: (() => {
            // margin sadece json_data içinde (proposal_items.margin alanı kaldırıldı)
            // CRITICAL: it.margin and b.margin are ALWAYS in percentage format (40) in UI
            // Convert to decimal format (1.4) for DB storage
            let marginForDb = 1.0;
            if (it.margin !== 0 && it.margin !== null && it.margin !== undefined) {
              const itemMargin = typeof it.margin === 'string' ? parseFloat(it.margin) : Number(it.margin);
              if (!isNaN(itemMargin)) {
                // UI always stores margin as percentage (40), convert to decimal (1.4) for DB
                if (itemMargin >= 1 && itemMargin < 2) {
                  // Edge case: already in decimal format, use as is
                  marginForDb = itemMargin;
                  console.warn('⚠️ Item margin already in decimal format:', itemMargin);
                } else {
                  // Normal case: percentage format (40), convert to decimal (1.4)
                  marginForDb = 1 + (itemMargin / 100);
                }
              }
            } else if (b.margin !== null && b.margin !== undefined) {
              const sectionMargin = typeof b.margin === 'string' ? parseFloat(b.margin) : Number(b.margin);
              if (!isNaN(sectionMargin)) {
                // UI always stores margin as percentage (40), convert to decimal (1.4) for DB
                if (sectionMargin >= 1 && sectionMargin < 2) {
                  // Edge case: already in decimal format
                  marginForDb = sectionMargin;
                  console.warn('⚠️ Section margin already in decimal format:', sectionMargin);
                } else {
                  // Normal case: percentage format (40), convert to decimal (1.4)
                  marginForDb = 1 + (sectionMargin / 100);
                }
              }
            }
            
            // Debug log for suspicious margin values
            if (marginForDb > 10 || marginForDb < 0.5 || isNaN(marginForDb)) {
              console.warn('⚠️ Suspicious marginForDb value:', {
                item_id: it.item_id,
                it_margin: it.margin,
                b_margin: b.margin,
                marginForDb
              });
            }
            return {
              uom_abbr: it.uom_abbr || null,
              margin: marginForDb,
              contractor_id: it.contractor_id || null,
              contractor: it.contractor || null,
              contractor_items: Array.isArray(it.contractor_items) ? it.contractor_items : [],
              contractor_total: it.contractor_total !== undefined && it.contractor_total !== null ? Number(it.contractor_total) : null,
              notes: it.notes || null,
              item_type: it.item_type ? it.item_type : (it.item_id ? 'product' : 'service'),
              name_en: (it.name_en !== undefined && it.name_en !== null && String(it.name_en).trim() !== '') ? String(it.name_en).trim() : null,
              name_de: (it.name_de !== undefined && it.name_de !== null && String(it.name_de).trim() !== '') ? String(it.name_de).trim() : null,
              sub_descriptions: Array.isArray(it.sub_descriptions) ? it.sub_descriptions : [],
              optional: !!it.optional,
              optional_hide_prices: !!it.optional_hide_prices,
              hide_price_note: it.hide_price_note || null,
              product_note: it.product_note || null,
              itemUsages: Object.entries(templateUsages).reduce((acc, [usageKey, usage]) => {
                const itemUsageKey = `${sectionBlocks.indexOf(b)}_${idxItem}_${usageKey}`;
                const customRatio = itemUsages[itemUsageKey]?.ratio;
                if (customRatio !== undefined && customRatio !== usage.ratio) {
                  acc[usageKey] = customRatio;
                }
                return acc;
              }, {})
            };
          })(),
        };
        })
      };
    });
    
    // Ortalama fiyat: templateUsages toplamlarini kullan
    const usageKeys = Object.keys(templateUsages || {});
    const usageTotals = usageKeys.reduce((acc, key) => {
      const total = sectionBlocks.reduce((sum, section, sIdx) => {
        return sum + (section.items || []).reduce((sub, item, iIdx) => {
          if (item?.optional) return sub;
          const calc = itemCalculations[`${sIdx}_${iIdx}`] || { usages: {} };
          return sub + Number(calc.usages?.[key] || 0);
        }, 0);
      }, 0);
      acc[key] = total;
      return acc;
    }, {});
    // Calculate total SLT for total_amount
    const totalSLT = sectionBlocks.reduce((sum, section, sIdx) => {
      return sum + (section.items || []).reduce((sub, item, iIdx) => {
        if (item?.optional) return sub;
        const calc = itemCalculations[`${sIdx}_${iIdx}`] || { slt: 0 };
        return sub + Number(calc.slt || 0);
      }, 0);
    }, 0);
    
    const avgPerDeployment = (() => {
      const totals = Object.values(usageTotals);
      if (!totals.length) return totalSLT;
      const sum = totals.reduce((a,b)=>a+Number(b||0),0);
      // Average = (SLT + all usages) / (1 + number of usages)
      return (totalSLT + sum) / (1 + totals.length);
    })();

    const payload = {
      title: title || `${selection.client_name || ''} - v${version || '1'}`,
      description: meta.subject || '', // Subject is stored in description field
      // CRITICAL: Convert 0 or empty client_id to null to avoid foreign key constraint errors
      client_id: (selection.client_id && Number(selection.client_id) > 0) ? Number(selection.client_id) : null,
      valid_until: proposal?.valid_until || null,
      status: proposal?.status || 'draft',
      currency: proposal?.currency || '',
      version_number: Number(version || 1),
      version_note: versionNote || null,
      template_id: Number(selection.template_id),
      proposal_type: proposalType, // 'offer' or 'proforma'
      show_detailed_pricing: showDetailedPricing ? 1 : 0,
      override_enabled: overrideEnabled ? 1 : 0,
      override_total: overrideEnabled && overrideTotal ? Number(overrideTotal) : null,
      template_json: {
        usages: templateUsages,
        sections: sectionBlocks.map(b => {
          // Section margin: UI'de yüzde formatında (40 gibi), DB'ye 1.xx formatında kaydedilmeli
          let sectionMarginForDb = 1.0;
          if (b.margin !== null && b.margin !== undefined) {
            sectionMarginForDb = b.margin >= 1 ? b.margin : (1 + (b.margin / 100));
          }
          return {
            section_id: b.section_id,
            name: b.section_name,
            margin: sectionMarginForDb
          };
        })
      },
      project: meta.project || null,
      offer_status: meta.offer_status || null,
      in_charge: Array.isArray(meta.in_charge) && meta.in_charge.length > 0 ? JSON.stringify(meta.in_charge) : null,
      notes: meta.notes || null,
      footer_text: (meta.footer_text && meta.footer_text.trim().length > 0) ? meta.footer_text : null,
      footer_signature_left: meta.footer_signature_left || null,
      footer_signature_right: meta.footer_signature_right || null,
      sections: sectionsPayload,
      total_amount: Number(totalSLT || 0),
      average_price_per_deployment: Number(avgPerDeployment || 0),
      items: sectionsPayload.flatMap(s => s.items)
    };

    console.log('💾 Edit payload:', payload);
    console.log('🔍 Sample item with optional:', payload.items.find(item => item.optional));
    // Debug: Check for suspicious values in payload
    const suspiciousItems = payload.items.filter(item => 
      Number(item.quantity || 0) > 1000000 || 
      Number(item.unit_price || 0) > 1000000 ||
      Number(item.line_total || 0) > 1000000000
    );
    if (suspiciousItems.length > 0) {
      console.error('🚨 CRITICAL: Suspicious values detected in payload:', suspiciousItems);
    }

    // Versiyon değiştiyse yeni teklif oluştur (yeni satır), aynıysa mevcut kaydı güncelle
    const currentVersion = Number(proposal?.version_number || 1);
    const nextVersion = Number(version || currentVersion);
    if (nextVersion !== currentVersion) {
      try {
        // Yeni versiyon: parent_id mevcut teklifin parent_id'si ya da kendisi olmalı
        const parentId = proposal?.parent_id || proposal?.id;
        await api.post('/api/proposals', { ...payload, parent_id: parentId });
        queryClient.invalidateQueries('proposals');
        toast({ title: t('common.success'), description: t('proposals.created_success'), status: 'success' });
        navigate('/proposals');
      } catch (e) {
        toast({ title: t('common.error'), description: e.response?.data?.error || t('messages.server_error'), status: 'error' });
      }
      return;
    }

    updateMutation.mutate(payload);
  };

  const [subDescModal, setSubDescModal] = useState({ isOpen: false, sectionIndex: null, itemIndex: null });
  const [noteModal, setNoteModal] = useState({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' });
  const [subDescQuery, setSubDescQuery] = useState('');
  const [subDescOptions, setSubDescOptions] = useState([]);
  const [subDescLoading, setSubDescLoading] = useState(false);
  const [subDescInput, setSubDescInput] = useState('');

  const loadSubDescriptions = async (item) => {
    try {
      setSubDescLoading(true);
      const type = item.item_type ? item.item_type : (item.item_id ? 'product' : 'service');
      const idParam = type === 'service' ? (item.service_id || item.item_id || item.id || 0) : (item.item_id || 0);
      const { data } = await api.get(`/api/meta/sub-descriptions`, { params: { item_type: type, item_id: idParam, q: subDescQuery } });
      setSubDescOptions(data?.data?.items || []);
    } catch (_) {
      setSubDescOptions([]);
    } finally {
      setSubDescLoading(false);
    }
  };

  const openSubDesc = (sectionIndex, itemIndex) => {
    const item = sectionBlocks[sectionIndex]?.items[itemIndex];
    setSubDescQuery('');
    setSubDescInput('');
    setSubDescOptions([]);
    setSubDescModal({ isOpen: true, sectionIndex, itemIndex });
    loadSubDescriptions(item);
  };

  const addSubDescription = async () => {
    const { sectionIndex, itemIndex } = subDescModal;
    if (subDescInput.trim().length === 0) return;
    const item = sectionBlocks[sectionIndex]?.items[itemIndex];
    const type = item.item_type ? item.item_type : (item.item_id ? 'product' : 'service');
    const idParam = type === 'service' ? (item.service_id || item.item_id || item.id || 0) : (item.item_id || 0);
    try {
      await api.post('/api/meta/sub-descriptions', { item_type: type, item_id: idParam, sub_description: subDescInput.trim() });
      // attach to row json
      setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
        ...b,
        items: b.items.map((x, k) => k === itemIndex ? ({
          ...x,
          sub_descriptions: Array.isArray(x.sub_descriptions) ? [...x.sub_descriptions, subDescInput.trim()] : [subDescInput.trim()]
        }) : x)
      }) : b));
      setSubDescInput('');
      toast({ title: t('common.success'), status: 'success' });
      loadSubDescriptions(item);
    } catch (e) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  const removeSubDescription = async (id) => {
    try {
      await api.delete(`/api/meta/sub-descriptions/${id}`);
      const item = sectionBlocks[subDescModal.sectionIndex]?.items[subDescModal.itemIndex];
      loadSubDescriptions(item);
      toast({ title: t('common.success'), status: 'success' });
    } catch (e) {
      toast({ title: t('common.error'), status: 'error' });
    }
  };

  if (isLoading) {
    return <Box p={4}>Loading...</Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <HStack spacing={4} align="center">
          <Heading size="lg">{t('proposals.edit')}</Heading>
          {/* Proposal Type Toggle */}
          <HStack spacing={2}>
            <Button 
              size="sm" 
              variant={proposalType === 'offer' ? 'solid' : 'outline'} 
              colorScheme={proposalType === 'offer' ? 'blue' : 'gray'}
              onClick={() => setProposalType('offer')}
            >
              {t('proposals.type.offer') || 'Offer'}
            </Button>
            <Button 
              size="sm" 
              variant={proposalType === 'proforma' ? 'solid' : 'outline'} 
              colorScheme={proposalType === 'proforma' ? 'blue' : 'gray'}
              onClick={() => setProposalType('proforma')}
            >
              {t('proposals.type.proforma') || 'Proforma'}
            </Button>
          </HStack>
          {/* Show Detailed Pricing Toggle */}
          <Switch
            isChecked={showDetailedPricing}
            onChange={(e) => setShowDetailedPricing(e.target.checked)}
            size="md"
          />
          <Text fontSize="sm">{showDetailedPricing ? (t('proposals.show_detailed_pricing') || 'Show detailed pricing') : (t('proposals.hide_detailed_pricing') || 'Hide detailed pricing')}</Text>
        </HStack>
        <Button variant="outline" onClick={() => handleExit('/proposals')}>
          {t('common.back') || 'Back'}
        </Button>
      </HStack>

      <Card bg={bgColor} border="1px" borderColor={borderColor} boxShadow="lg" borderRadius="xl" w="100%">
        <CardBody p={1} w="100%">
          <VStack spacing={1}  align="stretch" w="100%" pt={4} px={2}>
            {/* Row 1: Client, Template, Version (inline labels) */}
            <HStack align="flex-end" spacing={6} flexWrap="wrap" mb={2} p={2}>
              <Box minW="420px" flex={1}>
                <InlineField label={t('clients.title') || 'Clients'} required minLabelW="140px">
                  <HStack align="center">
                    <Box flex={1}>
                      <SearchableSelect
                    value={{ id: selection.client_id, name: selection.client_name, company: selection.client_company }}
                    returnObject
                    onChange={(opt) => setSelection({ ...selection, client_id: opt?.id || opt, client_name: opt?.name || '', client_company: opt?.company || '' })}
                    placeholder={t('common.select') || 'Select'}
                    searchPlaceholder={t('common.search') || 'Search'}
                    fetchUrl={'/api/users/clients?q='}
                    valueKey="id"
                    labelKey="company"
                    fallbackLabelKey="name"
                    minChars={0}
                    size="sm"
                    isRequired
                    isDisabled={false}
                      />
                    </Box>
                    <Button size="sm" onClick={async () => {
                      try {
                        setClientPreview(null);
                        if (selection.client_id) {
                          const { data } = await api.get(`/api/users/clients/${selection.client_id}`);
                          setClientPreview(data?.data?.client || null);
                        }
                      } catch (_) { setClientPreview(null); }
                      setClientPreviewOpen(true);
                    }}>{t('common.view') || 'View'}</Button>
                  </HStack>
                </InlineField>
              </Box>
              <Box minW="420px" flex={1}>
                <InlineField label={t('proposals.select_template') || 'Select Template'} required minLabelW="140px">
              <Select size="sm" value={selection.template_id} onChange={(e) => {
                    const id = e.target.value;
                    const tpl = templates?.find(t => String(t.id) === String(id));
                    setSelection({ ...selection, template_id: id, template_name: tpl?.name || '' });
                    
                  // Load template sections and usages from new JSON structure
                    try {
                      const templateData = tpl?.json || {};
                      const rawSections = templateData.sections || [];
                      const rawUsages = templateData.usages || {};
                      
                    // Set template usages
                      setTemplateUsages(rawUsages);
                      
                      if (Array.isArray(rawSections) && rawSections.length > 0) {
                        const mapped = rawSections.map((s) => {
                          // Support both old format (name) and new format (name_en, name_de)
                          const nameEn = s.name_en || s.name || s.section_name || s.title || 'Section';
                          const nameDe = s.name_de || s.name || s.section_name || s.title || 'Section';
                          const sectionName = currentLanguage === 'de' ? nameDe : nameEn;
                          return {
                            section_id: s.section_id || s.id || null,
                            section_name: sectionName,
                            name_en: nameEn,
                            name_de: nameDe,
                            margin: Number(s.margin || 1.0), // Use section-specific margin
                            items: []
                          };
                        });
                        setSectionBlocks(mapped);
                      } else {
                        setSectionBlocks([]);
                      }
                    } catch (_) {
                      setSectionBlocks([]);
                    setTemplateUsages({});
                    }

                    // Load formula rules for this template
                    if (id) {
                      loadFormulaRules(id);
                    }
                  }}>
                    <option value="">{t('common.select') || 'Select'}</option>
                    {templates?.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))}
                  </Select>
                </InlineField>
              </Box>
              <Box minW="240px">
                <InlineField label={t('proposals.version_code') || 'VERSION CODE'} minLabelW="140px">
                  <Input size="sm" value={versionNote || ''} onChange={(e)=> setVersionNote(e.target.value)} placeholder={t('proposals.version_code')||'VERSION CODE'} />
                </InlineField>
              </Box>
              <Box minW="160px">
                <InlineField label={t('proposals.version') || 'Version'} minLabelW="140px">
                  <Input size="sm" value={version} onChange={(e) => setVersion(e.target.value)} />
                </InlineField>
              </Box>
            </HStack>

            {/* Row 2: Proposal Title (inline label) - "Offers" input */}
            {proposal && (
              <Box p={2} mb={2}>
                <InlineField label={t('proposals.title') || 'Proposals'} minLabelW="140px">
                  <Input value={title} onChange={(e)=> setTitle(e.target.value)} placeholder={t('proposals.title')||'Title'} />
                </InlineField>
              </Box>
            )}

            {/* Meta expandable */}
            <Card bg={bgColor} border="1px" borderColor={borderColor}>
              <CardBody>
                <HStack justify="space-between" onClick={() => setMetaOpen(v=>!v)} cursor="pointer">
                  <Text fontWeight="bold">{t('proposals.meta') || 'Meta'}</Text>
                  <Text color="gray.500">{metaOpen ? '−' : '+'}</Text>
                </HStack>
                <Collapse in={metaOpen} animateOpacity>
                  <VStack mt={4} align="stretch" spacing={3}>
                    <InlineField label={t('proposals.meta.project') || 'Project'} minLabelW="140px">
                      <Input value={meta.project} onChange={(e)=> setMeta({...meta, project: e.target.value})} />
                    </InlineField>
                    {/* Subject isteme göre gizlendi */}
                    <InlineField label={t('proposals.meta.subject') || 'Subject'} minLabelW="140px">
                      <Input value={meta.subject} onChange={(e)=> setMeta({...meta, subject: e.target.value})} />
                    </InlineField>
                    <InlineField label={t('proposals.meta.offer_status') || 'Offer status'} minLabelW="140px">
                      <Input value={meta.offer_status} onChange={(e)=> setMeta({...meta, offer_status: e.target.value})} />
                    </InlineField>
                    <InlineField label={t('proposals.meta.in_charge') || 'In charge'} minLabelW="140px">
                      <VStack align="stretch" spacing={2}>
                        <Select 
                          value="" 
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value && !meta.in_charge.includes(value)) {
                              setMeta({...meta, in_charge: [...meta.in_charge, value]});
                            }
                            e.target.value = '';
                          }}
                        >
                          <option value="">{t('common.select')||'Select'}</option>
                          {userNames?.filter(u => !meta.in_charge.includes(u.full_name)).map(u=> (
                            <option key={u.id} value={u.full_name}>{u.full_name}</option>
                          ))}
                        </Select>
                        {meta.in_charge.length > 0 && (
                          <HStack spacing={2} flexWrap="wrap">
                            {meta.in_charge.map((name, idx) => (
                              <Tag key={idx} size="md" colorScheme="blue">
                                <TagLabel>{name}</TagLabel>
                                <TagCloseButton onClick={() => {
                                  setMeta({...meta, in_charge: meta.in_charge.filter((_, i) => i !== idx)});
                                }} />
                              </Tag>
                            ))}
                          </HStack>
                        )}
                      </VStack>
                    </InlineField>
                    <InlineField label="Notes" minLabelW="140px">
                      <Textarea value={meta.notes} onChange={(e)=> setMeta({...meta, notes: e.target.value})} rows={3} />
                    </InlineField>
                  </VStack>
                </Collapse>
              </CardBody>
            </Card>

            {/* Footer expandable */}
            {/* Footer açıklama alanı: en alta taşındı */}

            {/* Section seçimi ve blok ekleme */}
            {proposal && (
              <Box p={2} mb={2}>
                <InlineField label={t('proposals.select_section') || 'Select Section'} minLabelW="140px">
                  <HStack w="100%">
                    <Select 
                      key={`section-select-${currentLanguage}`}
                      size="sm" 
                      value={selectedSectionId} 
                      onChange={(e) => setSelectedSectionId(e.target.value)}
                    >
                      <option value="">{t('common.select') || 'Select'}</option>
                      {sections?.map(sec => {
                        const sectionName = currentLanguage === 'de' 
                          ? (sec.name_de || sec.name_en || 'Section')
                          : (sec.name_en || sec.name_de || 'Section');
                        return (
                          <option key={sec.id} value={sec.id}>{sectionName}</option>
                        );
                      })}
                    </Select>
                    <Button onClick={addSectionBlock} isDisabled={!selectedSectionId} size="sm" height="32px">{t('common.add') || 'Add'}</Button>
                  </HStack>
                </InlineField>
              </Box>
            )}

            {/* Eklenen section blokları */}
            <DndContext 
              sensors={sensors} 
              collisionDetection={closestCenter} 
              onDragEnd={handleSectionDragEnd}
            >
              <SortableContext items={sectionBlocks.map((_, i) => `section-${i}`)} strategy={verticalListSortingStrategy}>
                {sectionBlocks.map((blk, idx) => (
                  <SortableSection key={`section-${idx}`} id={`section-${idx}`}>
                    {({ dragListeners }) => (
                      <Card bg="white" border="1px" borderColor="gray.200" boxShadow="lg" borderRadius="xl" overflow="hidden" w="100%">
                <Box 
                  bg="linear-gradient(135deg,rgb(0, 0, 0) 0%,rgb(102, 102, 102) 100%)" 
                  py={1}
                  px={2}
                  position="relative"
                >
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(255,255,255,0.1)"
                    backdropFilter="blur(10px)"
                  />
                  <Flex justify="space-between" align="center" position="relative" zIndex={1}>
                    <HStack spacing={6}>
                      <Text 
                        fontWeight="bold" 
                        fontSize="xl" 
                        color="white"
                        textShadow="0 2px 4px rgba(0,0,0,0.3)"
                        cursor="grab"
                        _active={{ cursor: 'grabbing' }}
                        {...dragListeners}
                      >
                        {blk.section_name}
                      </Text>
                      <HStack spacing={3} px={4} py={1} borderRadius="full" backdropFilter="blur(10px)">
                        <Text fontSize="sm" color="white" fontWeight="medium">
                          Margin:
                        </Text>
                        <Input
                          size="xs"
                          type="number"
                          step="1"
                          min="0"
                          maxW="60px"
                          value={blk.margin === null || blk.margin === undefined ? '' : blk.margin}
                          onChange={(e) => setSectionBlocks(prev => prev.map((b, i) => 
                            i === idx ? { ...b, margin: e.target.value === '' ? null : Number(e.target.value) } : b
                          ))}
                          placeholder="0"
                          bg="white"
                          border="none"
                          _focus={{ boxShadow: "0 0 0 2px rgba(255,255,255,0.5)" }}

                        />
                        <Text fontSize="sm" color="white" fontWeight="medium">%</Text>
                      </HStack>
                    </HStack>
                    <IconButton
                      aria-label="delete section"
                      size="sm"
                      variant="ghost"
                      colorScheme="whiteAlpha"
                      color="white"
                      _hover={{ bg: "rgba(255,255,255,0.2)" }}
                      icon={<FiX />}
                      onClick={() => {
                        if (window.confirm(t('messages.confirm_delete'))) {
                          setSectionBlocks(prev => prev.filter((_, i) => i !== idx));
                        }
                      }}
                    />
                  </Flex>
                </Box>
                <CardBody bg="white" p={6} w="100%" overflow="visible">
                  <VStack align="stretch" spacing={4} gap={0} w="100%" overflow="visible">
                    <Box bg="blue.50" p={4} borderRadius="lg" border="2px dashed" borderColor="blue.200" overflow="visible">
                      <SearchableSelect
                        label={
                          <HStack>
                            <Text fontWeight="bold" color="blue.700" fontSize="md">
                              🔍 {t('inventory.select_item') || 'Select Product'}
                            </Text>
                            <Text fontSize="sm" color="blue.600">
                              - {t('inventory.search_hint') || 'Start typing to search products'}
                            </Text>
                          </HStack>
                        }
                        value={''}
                        returnObject
                        clearOnSelect
                        onChange={(opt) => {
                          if (!opt) return; // Don't add null/undefined items
                          const itemName = (currentLanguage === 'de') ? (opt?.name_de || opt?.name || String(opt)) : (opt?.name_en || opt?.name || String(opt));
                          const itemId = opt?.id || null;
                          const itemType = (opt?.__source && String(opt.__source).includes('/api/services')) ? 'service' : 'product';
                          const unitPrice = opt?.unit_price || 0;
                          const abbr = currentLanguage === 'de' ? (opt?.uom_abbr_de || opt?.uom) : (opt?.uom_abbr_en || opt?.uom);
                          setSectionBlocks(prev => prev.map((b, i) => i === idx ? {
                            ...b,
                            items: [...b.items, { 
                              item_id: itemId, 
                              name: itemName, 
                              name_en: opt?.name_en || opt?.name || '',
                              name_de: opt?.name_de || opt?.name || '',
                              quantity: 1, 
                              unit_price: unitPrice, 
                              uom_abbr: abbr, 
                              item_type: itemType,
                              ...(itemType === 'service' ? { service_id: itemId } : {})
                            }]
                          } : b));
                        }}
                        placeholder={t('inventory.search_placeholder') || 'Type product name, category or SKU...'}
                        searchPlaceholder={t('inventory.search_placeholder') || 'Type product name, category or SKU...'}
                        fetchUrls={['/api/inventory?q=', '/api/services?q=']}
                        allowCreate
                        onCreateOption={(label) => {
                          setCreateItemModal({ isOpen: true, label, sectionIndex: idx });
                        }}
                        valueKey="id"
                        labelKey={currentLanguage === 'de' ? 'name_de' : 'name_en'}
                        minChars={1}
                        size="md"
                      />
                    </Box>
                    {/* Tablo Başlığı */}
                    <Flex fontWeight="700" fontSize="sm" textAlign="center" gap={0} mt={4} w="100%" sx={{
                      '& > *': {
                        height: '60px !important',
                        minHeight: '60px !important',
                        maxHeight: '60px !important'
                      }
                    }}>
                      {/* Ana Bilgiler - Koyu Yeşil */}
                      <Box 
                        bg="green.600" 
                        p={3} 
                        display="flex" 
                        alignItems="center" 
                        h="60px" 
                        flex="1"
                        gap={0}
                      >
                        <Box w="60px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.pos') || 'Pos.'}</Text>
                        </Box>
                        <Box w="65px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.qty') || 'Quantity'}</Text>
                        </Box>
                        <Box w="120px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.unit') || 'Unit'}</Text>
                        </Box>
                        <Box w="240px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.description') || 'Description'}</Text>
                        </Box>
                        <Box w="120px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.sppu') || 'SPPU'}</Text>
                        </Box>
                        <Box w="120px" h="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center">
                          <Text color="white" fontWeight="extrabold">{t('table.slt') || 'SLT'}</Text>
                        </Box>
                      </Box>
                      
                      {/* Dynamic Usage Columns - Koyu Mavi */}
                      {Object.entries(templateUsages).map(([usageKey, usage]) => (
                        <Box 
                          key={usageKey} 
                          w="140px" 
                          textAlign="center" 
                          bg="blue.700" 
                          p={3} 
                          h="60px"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Text fontSize="sm" fontWeight="extrabold" color="white">
                            {currentLanguage === 'de' ? usage['lang.de'] : usage['lang.en']}
                          </Text>
                          <Text fontSize="xs" color="whiteAlpha.800" mt={1}>
                            ×{usage.ratio}
                          </Text>
                        </Box>
                      ))}
                      
                      {/* Diğer Kolonlar - Koyu Gri */}
                      <Box 
                        w="140px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.contractor') || 'Contractor'}</Text>
                      </Box>
                      <Box 
                        w="120px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.final_price') || 'Final Price'}</Text>
                      </Box>
                      <Box 
                        w="120px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.margin') || 'Margin'}</Text>
                      </Box>
                      <Box 
                        w="160px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.unit_price_x_amount') || 'Unit x Qty'}</Text>
                      </Box>
                      <Box 
                        w="140px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={2}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.unit_price') || 'UP'}</Text>
                      </Box>
                      <Box 
                        w="120px" 
                        textAlign="center" 
                        bg="gray.700"
                        p={1}
                        h="60px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="white" fontWeight="extrabold">{t('table.notes') || 'Notes'}</Text>
                      </Box>
                      <Box w="36px"></Box>
                    </Flex>
                    {/* Satırlar */}
                    <DndContext 
                      sensors={sensors} 
                      collisionDetection={closestCenter} 
                      onDragEnd={handleItemDragEnd(idx)}
                    >
                      <SortableContext items={blk.items.map((_, i) => `item-${idx}-${i}`)} strategy={verticalListSortingStrategy}>
                        {blk.items.map((it, jdx) => {
                          const calculationKey = `${idx}_${jdx}`;
                          const calculations = itemCalculations[calculationKey] || {
                            sppu: 0,
                            slt: 0,
                            final_price: 0,
                            unit_price_x_amount: 0,
                            usages: {}
                          };
                          
                          return (
                            <SortableItem key={`item-${idx}-${jdx}`} id={`item-${idx}-${jdx}`} isOptional={it?.optional}>
                              {({ dragListeners }) => (
                                <Flex align="stretch" gap={0} mb={1} w="100%" minH="60px" onContextMenu={(e)=>{
                          e.preventDefault();
                          setRowContextMenu({ open: true, x: e.clientX, y: e.clientY, sectionIndex: idx, itemIndex: jdx });
                        }}>
                          <Box 
                            bg={(it?.optional ? "black" : (jdx % 2 === 0 ? "green.50" : "green.100"))}
                            color={it?.optional ? "white" : undefined}
                            p={3} 
                            display="flex" 
                            alignItems="center" 
                          minH="48px" 
                          flex="1"
                            gap={0}
                          >
                            <Box w="60px" minH="60px" textAlign="center" display="flex" alignItems="center" justifyContent="center" cursor="grab" _active={{ cursor: 'grabbing' }} {...dragListeners}>
                              <Text fontSize="sm" fontWeight="medium" color={it?.optional ? "white" : "green.800"}>
                                {String(jdx + 1).padStart(2, '0')}
                              </Text>
                            </Box>
                            <Box w="65px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                              <Input 
                                type="number" 
                                min={1} 
                                value={it.quantity === 0 ? '' : it.quantity}
                                onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                                  ...b,
                                  items: b.items.map((x,k)=> k===jdx ? { ...x, quantity: e.target.value === '' ? 0 : Number(e.target.value) } : x)
                                } : b))}
                                size="sm"
                                bg="transparent"
                                border="none"
                                _focus={{ boxShadow: "none" }}
                                w="100%"
                                textAlign="center"
                                color={it?.optional ? "white" : undefined}
                              />
                            </Box>
                            <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                              {editingUnits[`${idx}_${jdx}`] ? (
                              <HStack spacing={1}>
                                  <Select
                                    value={it.uom_abbr || ''}
                                    onChange={(e) => {
                                      setSectionBlocks(prev => prev.map((b, i) => i === idx ? {
                                        ...b,
                                        items: b.items.map((x, k) => k === jdx ? { ...x, uom_abbr: e.target.value } : x)
                                      } : b));
                                      setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: false }));
                                    }}
                                    size="sm"
                                    autoFocus
                                  >
                                    <option value="">-</option>
                                    {units?.map(unit => (
                                      <option key={unit.id} value={currentLanguage === 'de' ? unit.abbreviation_de : unit.abbreviation_en}>
                                        {currentLanguage === 'de' ? unit.abbreviation_de : unit.abbreviation_en}
                                      </option>
                                    ))}
                                  </Select>
                                  <Input
                                    value={it.uom_abbr || ''}
                                    onChange={(e) => {
                                      setSectionBlocks(prev => prev.map((b, i) => i === idx ? {
                                        ...b,
                                        items: b.items.map((x, k) => k === jdx ? { ...x, uom_abbr: e.target.value } : x)
                                      } : b));
                                    }}
                                    onBlur={() => setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: false }))}
                                    onKeyPress={(e) => e.key === 'Enter' && setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: false }))}
                                    size="sm"
                                    placeholder="Manuel giriş"
                                  minW="60px"
                                  w="60px"
                                    isReadOnly={false}
                                  />
                                </HStack>
                              ) : (
                                <Box
                                  cursor="pointer"
                                  onClick={() => setEditingUnits(prev => ({ ...prev, [`${idx}_${jdx}`]: true }))}
                                  _hover={{ bg: it?.optional ? 'gray.800' : 'gray.100' }}
                                p={1}
                                  color={it?.optional ? "white" : undefined}
                                >
                                    {it.uom_abbr || '-'}
                                </Box>
                              )}
                            </Box>
                          <Box w="240px" minH="60px" display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center" p={2} position="relative" role="group" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {it?.optional && (
                              <Text fontSize="xs" fontWeight="extrabold" color="yellow.300" letterSpacing="0.06em" mb={1}>
                                {t('proposal.item.optional_label')?.toLocaleUpperCase('en-US') || 'OPTIONAL'}
                                {' '}
                                <Text as="span" fontSize="2xs" fontWeight="normal" color="yellow.200">
                                  ({it.optional_hide_prices ? t('proposal.item.no_prices', 'no prices') : t('proposal.item.with_prices', 'with prices')})
                                </Text>
                              </Text>
                            )}
                            {it?.product_note && (
                              <Text fontSize="xs" fontWeight="bold" color="blue.300" mb={1} cursor="pointer" onClick={() => setNoteModal({ isOpen: true, type: 'product_note', sectionIndex: idx, itemIndex: jdx, value: it.product_note })}>📝 {it.product_note}</Text>
                            )}
                            <Text textAlign="left" fontSize="sm">{currentLanguage === 'de' ? (it.name_de || it.name_en || '') : (it.name_en || it.name_de || '')}</Text>
                            <IconButton aria-label="add sub description" size="xs" variant="ghost" icon={<FiPlus />} onClick={() => openSubDesc(idx, jdx)} position="absolute" top="4px" right="4px" opacity={0} _groupHover={{ opacity: 1 }} />
                            {Array.isArray(it.sub_descriptions) && it.sub_descriptions.length > 0 && (
                              <HStack mt={2} spacing={1} wrap="wrap">
                                {it.sub_descriptions.map((sd, i2) => (
                                  <Tag key={i2} size="sm" colorScheme={String(sd).startsWith('**') ? 'blue' : 'gray'} variant="subtle"
                                    onContextMenu={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const isBold = String(sd).startsWith('**');
                                      const cleanText = String(sd).replace(/^\*\*/, '');
                                      setSectionBlocks(prev => prev.map((b, i) => i === idx ? ({
                                        ...b,
                                        items: b.items.map((x, k) => k === jdx ? ({
                                          ...x,
                                          sub_descriptions: (x.sub_descriptions || []).map((v, si) => si === i2 ? (isBold ? cleanText : '**' + cleanText) : v)
                                        }) : x)
                                      }) : b));
                                    }}
                                  >
                                    <TagLabel fontWeight={String(sd).startsWith('**') ? 'bold' : 'normal'}>+ {String(sd).replace(/^\*\*/, '')}</TagLabel>
                                    <TagCloseButton onClick={() => {
                                      setSectionBlocks(prev => prev.map((b, i) => i === idx ? ({
                                        ...b,
                                        items: b.items.map((x, k) => k === jdx ? ({
                                          ...x,
                                          sub_descriptions: (x.sub_descriptions || []).filter((v, si) => !(si === i2 && v === sd))
                                        }) : x)
                                      }) : b));
                                    }} />
                                  </Tag>
                                ))}
                              </HStack>
                            )}
                          </Box>
                          <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center">
                            <Text fontSize="sm" fontWeight="medium" color={it?.optional ? 'white' : undefined} textAlign="center">
                              {formatCurrency(calculations.sppu || 0)}
                            </Text>
                          </Box>
                          <Box w="120px" minH="60px" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                            <Text fontSize="sm" fontWeight="medium" color={it?.optional ? 'white' : undefined} textAlign="center">
                              {formatCurrency(calculations.slt || 0)}
                            </Text>
                            {it?.hide_price_note && (
                              <Text fontSize="2xs" color="red.400" textAlign="center" px={1} lineHeight="1.2" cursor="pointer" onClick={() => setNoteModal({ isOpen: true, type: 'hide_price_note', sectionIndex: idx, itemIndex: jdx, value: it.hide_price_note })}>⚠ {it.hide_price_note}</Text>
                            )}
                          </Box>
                            </Box>
                          {/* Dynamic Usage Columns */}
                          {Object.entries(templateUsages).map(([usageKey, usage]) => {
                          const usageValue = calculations.usages[usageKey] || 0;
                            const itemUsageKey = `${idx}_${jdx}_${usageKey}`;
                          const isEditingUsage = editingUsage === itemUsageKey;
                          const customRatio = itemUsages[itemUsageKey]?.ratio || usage.ratio;
                            
                            return (
                            <Box key={usageKey} w="140px" minH="60px" textAlign="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "blue.50" : "blue.100")}  borderColor="blue.200" color={it?.optional ? "white" : undefined} display="flex" alignItems="center" justifyContent="center">
                          {isEditingUsage ? (
                                <Box
                                  bg={jdx % 2 === 0 ? "blue.50" : "blue.100"}
                                    minH="60px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    w="100%"
                                  >
                                    <Input
                                      size="sm"
                                      type="number"
                                      step="0.01"
                                      min="0"
                                    value={customRatio === 0 ? '' : customRatio}
                                      onChange={(e) => setItemUsages(prev => ({
                                        ...prev,
                                        [itemUsageKey]: { ...prev[itemUsageKey], ratio: e.target.value === '' ? 0 : Number(e.target.value) }
                                      }))}
                                      onBlur={() => setEditingUsage(null)}
                                      onKeyPress={(e) => e.key === 'Enter' && setEditingUsage(null)}
                                      autoFocus
                                      maxW="70px"
                                      bg="white"
                                      _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                                      textAlign="center"
                                    />
                                  </Box>
                                ) : (
                                  <Box
                                    cursor="pointer"
                                    onClick={() => setEditingUsage(itemUsageKey)}
                                    _hover={{ bg: it?.optional ? 'gray.900' : 'blue.50', transform: "scale(1.02)" }}
                                    borderRadius={it?.optional ? 'none' : 'md'}
                                    minH="60px"
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    transition="all 0.2s"
                                    position="relative"
                                    w="100%"
                                  >
                                    <Text fontSize="sm" fontWeight="medium" color={it?.optional ? "white" : "blue.700"}>
                                      {formatCurrency(usageValue || 0)}
                                    </Text>
                                    {/* Only show ratio if it's different from template default */}
                                  {customRatio !== usage.ratio && (
                                      <Box
                                        position="absolute"
                                        bottom="4px"
                                        left="50%"
                                        transform="translateX(-50%)"
                                        bg="blue.100"
                                        borderRadius="full"
                                        px={2}
                                        py={0.5}
                                        display="inline-block"
                                        boxShadow="sm"
                                        zIndex={1}
                                      >
                                        <Text fontSize="9px" color={it?.optional ? "white" : "blue.700"} fontWeight="bold">
                                          ×{customRatio}
                                        </Text>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        <Box w="140px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? 'black' : (jdx % 2 === 0 ? "gray.50" : "gray.100")} borderColor="gray.200" color={it?.optional ? 'white' : undefined}>
                          <Box
                            cursor="pointer"
                            onClick={() => openContractorModal(idx, jdx)}
                            _hover={{ bg: it?.optional ? 'gray.900' : 'gray.200' }}
                            p={2}
                              borderRadius={it?.optional ? 'none' : 'md'}
                              w="100%"
                            minH="60px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text fontSize="sm" color={it?.optional ? 'white' : 'gray.700'}>
                              {it.contractor || '-'}
                            </Text>
                            {it.contractor_items && it.contractor_items.length > 0 && (
                              <Text fontSize="xs" color={it?.optional ? 'gray.300' : "green.600"} ml={1}>
                                ({it.contractor_items.length})
                              </Text>
                            )}
                          </Box>
                        </Box>
                        <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")} borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                            <Input 
                            value={formatCurrency(calculations.final_price || 0)}
                              isReadOnly 
                              size="sm"
                            bg="transparent"
                            color={it?.optional ? 'white' : undefined}
                            border="none"
                              w="100%"
                            textAlign="center"
                            _focus={{ boxShadow: "none" }}
                            />
                          </Box>
                                                <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? 'black' : (jdx % 2 === 0 ? "gray.50" : "gray.100")}  borderColor="gray.200" color={it?.optional ? 'white' : undefined}>
                            <Input 
                              type="number" 
                              step="1" 
                              placeholder={(blk.margin !== null && blk.margin !== undefined) ? String(blk.margin) : "0"}
                              value={(() => {
                                const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                                return marginValue === 0 ? '0' : (marginValue !== null && marginValue !== undefined ? marginValue : '');
                              })()}
                              onFocus={(e) => {
                                const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                                if (marginValue === 0) {
                                  e.target.select();
                                  setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                                    ...b,
                                    items: b.items.map((x,k)=> k===jdx ? { ...x, margin: null } : x)
                                  } : b));
                                }
                              }}
                              onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                                ...b,
                                items: b.items.map((x,k)=> k===jdx ? { ...x, margin: e.target.value === '' ? null : Number(e.target.value) } : x)
                              } : b))}
                              size="sm"
                              bg="transparent"
                              color={(() => {
                                const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                                if (marginValue === 0) return '#ff0000';
                                return it?.optional ? 'white' : undefined;
                              })()}
                              fontWeight={(() => {
                                const marginValue = (it.margin !== null && it.margin !== undefined) ? it.margin : (blk.margin !== null && blk.margin !== undefined ? blk.margin : 0);
                                return marginValue === 0 ? 700 : undefined;
                              })()}
                              border="none"
                              w="100%"
                            textAlign="center"
                            _focus={{ boxShadow: "none" }}
                            _placeholder={{ color: 'gray.400' }}
                            />
                          </Box>
                        <Box w="160px" minH="60px" textAlign="center" fontSize="sm" fontWeight="medium" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")} borderColor="gray.200" color={it?.optional ? "white" : "gray.700"}>
                          {formatCurrency(it.contractor_total ? it.contractor_total : (calculations.unit_price_x_amount || 0))}
                          </Box>
                        <Box w="140px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")}  borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                            <Input 
                              type="number" 
                              min={0} 
                              value={it.unit_price === 0 ? '' : it.unit_price}
                              onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                                ...b,
                                items: b.items.map((x,k)=> k===jdx ? { ...x, unit_price: e.target.value === '' ? 0 : Number(e.target.value) } : x)
                              } : b))}
                              size="sm"
                          bg="transparent"
                          color={it?.optional ? 'white' : undefined}
                          border="none"
                              w="100%"
                          textAlign="center"
                          _focus={{ boxShadow: "none" }}
                            />
                          </Box>
                        <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg={it?.optional ? "black" : (jdx % 2 === 0 ? "gray.50" : "gray.100")}  borderColor="gray.200" color={it?.optional ? "white" : undefined}>
                            <Input 
                              value={it.notes || ''} 
                              placeholder={t('common.add_note') || 'Add note...'}
                              onChange={(e) => setSectionBlocks(prev => prev.map((b, i)=> i===idx ? {
                                ...b,
                                items: b.items.map((x,k)=> k===jdx ? { ...x, notes: e.target.value } : x)
                              } : b))}
                              size="sm"
                            bg="transparent"
                            color={it?.optional ? 'white' : undefined}
                            _placeholder={it?.optional ? { color: 'gray.300' } : undefined}
                            border="none"
                              w="100%"
                            textAlign="center"
                            _focus={{ boxShadow: "none" }}
                            />
                          </Box>

                        <Box w="36px" textAlign="right">
                          <IconButton aria-label="delete row" size="xs" variant="ghost" colorScheme="red" color="red.500" _hover={{ color: 'red.600' }} icon={<FiX />} onClick={() => {
                                if (window.confirm(t('messages.confirm_delete'))) {
                                  setSectionBlocks(prev => prev.map((b,i)=> i===idx ? {
                                    ...b,
                                    items: b.items.filter((_,k)=> k!==jdx)
                                  } : b));
                                }
                          }} />
                          </Box>
                              </Flex>
                              )}
                            </SortableItem>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                    {/* Section totals - only under GP (SLT) and usage columns, plain bold text */}
                    <Flex align="stretch" mb={1} w="100%" minH="60px" gap={0}>
                      {/* Placeholders until SLT column */}
                      <Box flex="1" p={3} display="flex" gap={0} alignItems="center" bg="green.50">
                        <Box w="60px" minH="60px" bg="green.50" />
                        <Box w="65px" minH="60px" bg="green.50" />
                        <Box w="120px" minH="60px" bg="green.50" />
                        <Box w="240px" minH="60px" bg="green.50" /> {/* Description column - 240px */}
                        <Box w="120px" minH="60px" bg="green.50" /> {/* SPPU column */}
                        <Box w="120px" minH="60px" display="flex" alignItems="center" justifyContent="center" bg="green.50">
                          <Text fontWeight="extrabold" textAlign="center" w="100%" fontSize="sm">
                            {formatCurrency((blk.items || []).reduce((sum, item, itemIdx) => {
                              if (item?.optional) return sum; // Skip optional items
                              const key = `${idx}_${itemIdx}`;
                              const calc = itemCalculations[key] || { slt: 0 };
                              return sum + Number(calc.slt || 0);
                            }, 0))}
                          </Text>
                        </Box>
                      </Box>

                      {Object.entries(templateUsages).map(([usageKey]) => {
                        const usageTotal = (blk.items || []).reduce((sum, item, itemIdx) => {
                          if (item?.optional) return sum; // Skip optional items
                          const key = `${idx}_${itemIdx}`;
                          const calc = itemCalculations[key] || { usages: {} };
                          return sum + Number(calc.usages?.[usageKey] || 0);
                        }, 0);
                        return (
                          <Box key={usageKey} w="140px" minH="60px" p={3} display="flex" alignItems="center" justifyContent="center" bg="blue.50">
                            <Text fontWeight="extrabold" textAlign="center" w="100%" fontSize="sm">{formatCurrency(usageTotal)}</Text>
                          </Box>
                        );
                      })}

                      {/* Placeholders for remaining columns */}
                      <Box w="140px" minH="60px" p={3} bg="gray.50" />
                      <Box w="120px" minH="60px" p={3} bg="gray.50" />
                      <Box w="120px" minH="60px" p={3} bg="gray.50" />
                      <Box w="160px" minH="60px" p={3} bg="gray.50" />
                      <Box w="140px" minH="60px" p={3} bg="gray.50" />
                      <Box w="120px" minH="60px" p={3} bg="gray.50" />
                      <Box w="36px" minH="60px" bg="transparent" />
                    </Flex>
                  </VStack>
                </CardBody>
                    </Card>
                    )}
                  </SortableSection>
                ))}
              </SortableContext>
            </DndContext>
          </VStack>
        </CardBody>
      </Card>
      {/* Global totals summary below all sections */}
      {sectionBlocks.length > 0 && (
        <Card bg={bgColor} border="1px" borderColor={borderColor} boxShadow="md" borderRadius="xl" mt={4}>
          <CardBody>
            {(() => {
              const totalSLT = sectionBlocks.reduce((sum, section, sIdx) => {
                return sum + (section.items || []).reduce((sub, item, iIdx) => {
                  if (item?.optional) return sub; // Skip optional items
                  const key = `${sIdx}_${iIdx}`;
                  const calc = itemCalculations[key] || { slt: 0 };
                  return sub + Number(calc.slt || 0);
                }, 0);
              }, 0);

              const usageTotals = Object.keys(templateUsages).reduce((acc, usageKey) => {
                const total = sectionBlocks.reduce((sum, section, sIdx) => {
                  return sum + (section.items || []).reduce((sub, item, iIdx) => {
                    if (item?.optional) return sub; // Skip optional items
                    const key = `${sIdx}_${iIdx}`;
                    const calc = itemCalculations[key] || { usages: {} };
                    return sub + Number(calc.usages?.[usageKey] || 0);
                  }, 0);
                }, 0);
                acc[usageKey] = total;
                return acc;
              }, {});

              return (
                <VStack align="stretch" spacing={4}>
                  <Flex align="center" gap={0} w="100%" sx={{
                    '& > *': { height: '48px', minHeight: '48px' }
                  }}>
                    {/* Left grouped columns with label under Description */}
                    <Box flex="1" display="flex" gap={0} alignItems="center">
                      <Box w="60px" />
                      <Box w="65px" />
                      <Box w="120px" />
                      <Box w="120px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontWeight="bold">{t('proposal.page.total') || 'TOTAL:'}</Text>
                      </Box>
                      <Box w="120px" /> {/* SPPU total not shown */}
                      <Box w="120px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontWeight="extrabold">{formatCurrency(totalSLT)}</Text>
                      </Box>
                    </Box>

                    {/* Usage totals aligned to their columns */}
                    {Object.entries(templateUsages).map(([usageKey]) => (
                      <Box key={usageKey} w="140px" display="flex" alignItems="center" justifyContent="center">
                        <Text fontWeight="extrabold">{formatCurrency(usageTotals[usageKey] || 0)}</Text>
                      </Box>
                    ))}

                    {/* Remaining columns placeholders */}
                    <Box w="140px" />
                    <Box w="120px" />
                    <Box w="120px" />
                    <Box w="160px" />
                    <Box w="140px" />
                    <Box w="120px" />
                    <Box w="36px" />
                  </Flex>

                  {/* Cost overview table */}
                  <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>{t('proposal.page.deployments') || 'Deployments'}</Th>
                          <Th isNumeric>{t('proposal.page.costs') || 'Cost Overview'}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {/* SLT first */}
                        <Tr>
                          <Td>{t('table.slt') || 'SLT'}</Td>
                          <Td isNumeric>{formatCurrency(totalSLT)}</Td>
                        </Tr>
                        {Object.entries(templateUsages).map(([usageKey, usage], i) => (
                          <Tr key={usageKey}>
                            <Td>{(currentLanguage === 'de' ? (usage['lang.de'] || `Einsatz-${i+1}`) : (usage['lang.en'] || `Deployment-${i+1}`))}</Td>
                            <Td isNumeric>{formatCurrency(usageTotals[usageKey] || 0)}</Td>
                          </Tr>
                        ))}
                        <Tr fontWeight="bold">
                          <Td>{t('proposal.page.grand_total_deployments', { params: { count: 1 + Object.keys(templateUsages).length } }) || `Total for ${1 + Object.keys(templateUsages).length} deployments`}</Td>
                          <Td isNumeric>{formatCurrency(totalSLT + Object.values(usageTotals).reduce((a,b)=>a+Number(b||0),0))}</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Average per deployment */}
                  <HStack justify="space-between" bg="black" color="white" px={4} py={3} borderRadius="md">
                    <Text fontWeight="bold">{t('proposal.page.average_price_per_deployment') || 'Average price per deployment'}</Text>
                    <Text fontWeight="extrabold" fontSize="xl">
                      {formatCurrency(averagePricePerUsage)}
                    </Text>
                  </HStack>
                </VStack>
              );
            })()}
          </CardBody>
        </Card>
      )}

      {/* Footer açıklama alanı - en altta */
      }
      <Card bg={bgColor} border="1px" borderColor={borderColor} mt={3}>
        <CardBody>
          <HStack justify="space-between" onClick={() => setFooterOpen(v=>!v)} cursor="pointer">
            <Text fontWeight="bold">Footer</Text>
          </HStack>
          <Collapse in={footerOpen} animateOpacity>
            <VStack mt={4} align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Footer Text</FormLabel>
                <FooterEditor value={meta.footer_text} onChange={(val)=> setMeta({...meta, footer_text: val})} />
              </FormControl>
            </VStack>
          </Collapse>
        </CardBody>
      </Card>
      {/* Free Override Section */}
      {proposal && (
        <Card mt={4} variant="outline">
          <CardBody py={3}>
            <HStack spacing={4} align="center">
              <Checkbox
                isChecked={overrideEnabled}
                onChange={(e) => setOverrideEnabled(e.target.checked)}
                colorScheme="orange"
              >
                <Text fontWeight="medium">{t('proposal.override_total', 'Free Override')}</Text>
              </Checkbox>
              {overrideEnabled && (
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">{t('proposal.override_total_label', 'Custom Total')}:</Text>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={overrideTotal}
                    onChange={(e) => setOverrideTotal(e.target.value)}
                    placeholder="0.00"
                    w="150px"
                    size="sm"
                  />
                  <Text fontSize="sm" color="gray.500">{proposal?.currency || 'EUR'}</Text>
                </HStack>
              )}
              {overrideEnabled && overrideTotal && (
                <Text fontSize="sm" color="orange.600" fontWeight="medium">
                  {t('proposal.override_note', 'Original total will be crossed out in PDF')}
                </Text>
              )}
            </HStack>
          </CardBody>
        </Card>
      )}

      {proposal && (
        <HStack justify="flex-end" mt={4}>
          <Button
            leftIcon={<FiDollarSign />}
            colorScheme="green"
            variant="outline"
            onClick={() => setInvoiceModalOpen(true)}
          >
            {t('invoices.title', { defaultValue: 'Invoices & Payments' })}
          </Button>
          <Button colorScheme="brand" onClick={handleSave} isLoading={updateMutation?.isLoading}>
            {t('common.save') || 'Save'}
          </Button>
        </HStack>
      )}

      {/* New Supplier Modal */}
      <Modal isOpen={newSupplierModal} onClose={() => {
        setNewSupplierModal(false);
        setNewSupplierForm({
          name: '',
          email: '',
          phone: '',
          address: '',
          category: '',
          rating: '',
          notes: ''
        });
      }} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('common.new')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <FormControl isRequired>
                <FormLabel>{t('common.name')}</FormLabel>
                <Input value={newSupplierForm.name} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, name: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.email')}</FormLabel>
                  <Input value={newSupplierForm.email} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, email: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('common.phone')}</FormLabel>
                  <Input value={newSupplierForm.phone} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, phone: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('common.address') || 'Address'}</FormLabel>
                <Textarea value={newSupplierForm.address} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, address: e.target.value })} />
              </FormControl>
              <HStack w="100%">
                <FormControl>
                  <FormLabel>{t('common.category') || 'Category'}</FormLabel>
                  <Input value={newSupplierForm.category} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, category: e.target.value })} />
                </FormControl>
                <FormControl>
                  <FormLabel>{t('common.rating') || 'Rating'}</FormLabel>
                  <Input type="number" value={newSupplierForm.rating} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, rating: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel>{t('common.notes') || 'Notes'}</FormLabel>
                <Textarea value={newSupplierForm.notes} onChange={(e) => setNewSupplierForm({ ...newSupplierForm, notes: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} variant="ghost" onClick={() => {
              setNewSupplierModal(false);
              setNewSupplierForm({
                name: '',
                email: '',
                phone: '',
                address: '',
                category: '',
                rating: '',
                notes: ''
              });
            }}>{t('common.cancel')}</Button>
            <Button colorScheme="brand" onClick={saveNewSupplier}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Contractor Modal */}
      <Modal isOpen={contractorModal.isOpen} onClose={() => {
        setContractorModal({ isOpen: false, sectionIndex: null, itemIndex: null });
        setContractorSearch('');
        setContractorSuggestions([]);
        setShowContractorSuggestions(false);
      }} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Text>{t('table.contractor') || 'Contractor'} Management</Text>
              <Text fontSize="sm" color="gray.500">- Row {contractorModal.itemIndex !== null ? contractorModal.itemIndex + 1 : ''}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Contractor Selection */}
              <Box position="relative">
                <FormLabel fontWeight="bold" mb={2}>{t('table.contractor') || 'Contractor'}</FormLabel>
                <HStack>
                  <Input
                    placeholder="Search contractor name..."
                    value={contractorSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContractorSearch(value);
                      setSelectedContractor(value);
                      searchContractors(value);
                    }}
                    onFocus={() => {
                      if (contractorSearch.length >= 2) {
                        setShowContractorSuggestions(true);
                      }
                    }}
                    size="md"
                  />
                  <Button
                    colorScheme="brand"
                    size="md"
                    onClick={() => setNewSupplierModal(true)}
                    leftIcon={<Text fontSize="lg">+</Text>}
                  >
                    {t('common.new')}
                  </Button>
                </HStack>
                
                {/* Contractor Suggestions Dropdown */}
                {showContractorSuggestions && contractorSuggestions.length > 0 && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="lg"
                    zIndex={1000}
                    maxH="200px"
                    overflowY="auto"
                  >
                    {contractorSuggestions.map((contractor) => (
                      <Box
                        key={contractor.id}
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: "blue.50" }}
                        onClick={() => selectContractor(contractor)}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <Text fontWeight="medium">{contractor.name}</Text>
                        {contractor.email && (
                          <Text fontSize="sm" color="gray.600">{contractor.email}</Text>
                        )}
                        {contractor.category && (
                          <Text fontSize="xs" color="blue.600">Category: {contractor.category}</Text>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Contractor Items Table */}
              <Box>
                <HStack justify="space-between" mb={3}>
                  <Text fontWeight="bold">{t('proposals.items') || 'Items'}</Text>
                  <Button size="sm" colorScheme="blue" onClick={addContractorItem} leftIcon={<FiPlus />}>
                    {t('common.add') || 'Add'} Item
                  </Button>
                </HStack>
                
                <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                  <Table size="sm" variant="simple">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th>{t('table.description') || 'Description'}</Th>
                        <Th>{t('table.qty') || 'Qty'}</Th>
                        <Th>{t('table.unit_price') || 'Unit Price'}</Th>
                        <Th>{t('table.final_price') || 'Total'}</Th>
                        <Th w="80px">Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {contractorItems.map((item, index) => (
                        <Tr key={index}>
                          <Td>
                            <Input
                              value={item.name}
                              onChange={(e) => updateContractorItem(index, 'name', e.target.value)}
                              placeholder="Item/Service name..."
                              size="sm"
                            />
                          </Td>
                          <Td>
                            <NumberInput
                              value={item.quantity}
                              onChange={(value) => updateContractorItem(index, 'quantity', Number(value))}
                              min={1}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td>
                            <NumberInput
                              value={item.unit_price}
                              onChange={(value) => updateContractorItem(index, 'unit_price', Number(value))}
                              min={0}
                              step={0.01}
                              size="sm"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </Td>
                          <Td>
                            <Text fontWeight="medium" color="blue.600">
                              {formatCurrency(item.total_price || 0)}
                            </Text>
                          </Td>
                          <Td>
                            <IconButton
                              aria-label="remove item"
                              icon={<FiX />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => removeContractorItem(index)}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                {/* Total Calculation */}
                {contractorItems.length > 0 && (
                  <Box mt={4} p={4} bg="blue.50" borderRadius="md">
                    <HStack justify="space-between">
                      <Text fontWeight="bold" color="blue.700">
                        {t('table.final_price') || 'Total Amount'}:
                      </Text>
                      <Text fontSize="xl" fontWeight="bold" color="blue.700">
                        {formatCurrency(contractorItems.reduce((sum, item) => sum + (item.total_price || 0), 0))}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={() => setContractorModal({ isOpen: false, sectionIndex: null, itemIndex: null })}>
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button colorScheme="blue" onClick={saveContractorData} isDisabled={contractorItems.length === 0}>
                {t('common.save') || 'Save'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={clientPreviewOpen} onClose={() => setClientPreviewOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('clients.details', { defaultValue: 'Client details' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={2}>
              <Text><b>{t('clients.company',{defaultValue:'Company'})}:</b> {selection.client_company || '-'}</Text>
              <Text><b>{t('clients.contact',{defaultValue:'Contact'})}:</b> {selection.client_name || '-'}</Text>
              <Text><b>{t('common.email')}:</b> {clientPreview?.email || '-'}</Text>
              <Text><b>{t('common.phone')}:</b> {clientPreview?.phone || '-'}</Text>
              <Text><b>{t('common.address')}:</b> {clientPreview?.address || '-'}</Text>
              <Text><b>{t('common.notes')}:</b> {clientPreview?.notes || '-'}</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setClientPreviewOpen(false)}>{t('common.close') || 'Close'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Sub Description Modal */}
      <Modal isOpen={subDescModal.isOpen} onClose={() => setSubDescModal({ isOpen:false, sectionIndex:null, itemIndex:null })} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr="72px">
            <HStack justify="space-between">
              <Text>{t('table.description') || 'Description'} – Sub items</Text>
              <Button size="sm" colorScheme="blue" mr={2} onClick={() => {
                const { sectionIndex, itemIndex } = subDescModal;
                const subList = (subDescOptions || []).map(x => x.sub_description).filter(Boolean);
                if (subList.length === 0) return;
                setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                  ...b,
                  items: b.items.map((it, k) => k === itemIndex ? ({
                    ...it,
                    sub_descriptions: Array.isArray(it.sub_descriptions) ? Array.from(new Set([...it.sub_descriptions, ...subList])) : Array.from(new Set(subList))
                  }) : it)
                }) : b));
                toast({ title: t('common.success'), status: 'success' });
                setSubDescModal({ isOpen:false, sectionIndex:null, itemIndex:null });
              }}>
                {t('common.add_all') || 'Add all'}
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Input placeholder={t('common.search') || 'Search'} value={subDescQuery} onChange={(e)=>{ setSubDescQuery(e.target.value); const item = sectionBlocks[subDescModal.sectionIndex]?.items[subDescModal.itemIndex]; loadSubDescriptions(item); }} />
              <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" maxH="260px" overflowY="auto" p={2}>
                {subDescLoading ? (
                  <Text fontSize="sm" color="gray.500">Loading...</Text>
                ) : (
                  (subDescOptions || []).map(opt => (
                    <HStack key={opt.id} p={2} _hover={{ bg:'gray.50' }} justify="space-between">
                      <Text fontSize="sm" flex={1}>+ {opt.sub_description}</Text>
                      <HStack spacing={2}>
                        <Button size="xs" onClick={() => {
                          const { sectionIndex, itemIndex } = subDescModal;
                          const val = opt.sub_description || '';
                          if (!val) return;
                          setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                            ...b,
                            items: b.items.map((it, k) => k === itemIndex ? ({
                              ...it,
                              sub_descriptions: Array.isArray(it.sub_descriptions) ? Array.from(new Set([...it.sub_descriptions, val])) : [val]
                            }) : it)
                          }) : b));
                        }}>
                          {t('common.add') || 'Add'}
                        </Button>
                        <IconButton 
                          aria-label="delete" 
                          size="sm" 
                          variant="ghost" 
                          colorScheme="red" 
                          icon={<FiTrash2 />} 
                          onClick={() => removeSubDescription(opt.id)} 
                        />
                      </HStack>
                    </HStack>
                  ))
                )}
              </Box>
              <Input placeholder={t('common.add') || 'Add sub description'} value={subDescInput} onChange={(e)=> setSubDescInput(e.target.value)} />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={()=> setSubDescModal({ isOpen:false, sectionIndex:null, itemIndex:null })}>{t('common.close') || 'Close'}</Button>
              <Button colorScheme="blue" onClick={addSubDescription} isDisabled={!subDescInput.trim()}> {t('common.save') || 'Save'} </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Invoices & Payments Modal */}
      <Modal isOpen={invoiceModalOpen} onClose={() => setInvoiceModalOpen(false)} size="5xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('invoices.title') || 'Invoices & Payments'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {invoiceSummary ? (
              <VStack align="stretch" spacing={6}>
                <Card>
                  <CardBody>
                    <HStack justify="space-between">
                      <Text fontWeight="bold">{invoiceSummary.proposal?.title}</Text>
                      <HStack>
                        <Text>{t('invoices.total_proposal') || 'Proposal Total'}:</Text>
                        <Text fontWeight="bold">{formatCurrency(invoiceSummary.summary.proposal_total || 0)}</Text>
                      </HStack>
                    </HStack>
                    <HStack mt={3} spacing={8}>
                      <HStack>
                        <Text color="gray.600">{t('invoices.total_invoiced') || 'Invoiced'}:</Text>
                        <Text fontWeight="bold">{formatCurrency(invoiceSummary.summary.total_invoiced || 0)}</Text>
                      </HStack>
                      <HStack>
                        <Text color="gray.600">{t('invoices.total_paid') || 'Paid'}:</Text>
                        <Text fontWeight="bold">{formatCurrency(invoiceSummary.summary.total_paid || 0)}</Text>
                      </HStack>
                      <HStack>
                        <Text color="gray.600">{t('invoices.remaining_to_invoice') || 'Remaining to invoice'}:</Text>
                        <Text fontWeight="bold">{formatCurrency(invoiceSummary.summary.remaining_to_invoice || 0)}</Text>
                      </HStack>
                      <HStack>
                        <Text color="gray.600">{t('invoices.outstanding_balance') || 'Outstanding'}:</Text>
                        <Text fontWeight="bold">{formatCurrency(invoiceSummary.summary.outstanding_balance || 0)}</Text>
                      </HStack>
                    </HStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>{t('invoices.number') || 'Number'}</Th>
                          <Th>{t('invoices.type') || 'Type'}</Th>
                          <Th isNumeric>{t('invoices.amount') || 'Amount'}</Th>
                          <Th>{t('invoices.status') || 'Status'}</Th>
                          <Th>{t('invoices.date') || 'Date'}</Th>
                          <Th w="320px">{t('payments.title') || 'Payments'}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(invoiceSummary.invoices || []).map(inv => (
                          <Tr key={inv.id}>
                            <Td>{inv.invoice_number}</Td>
                            <Td>{inv.invoice_type}</Td>
                            <Td isNumeric>{formatCurrency(inv.invoiced_amount || inv.total_amount || 0)}</Td>
                            <Td>{inv.status}</Td>
                            <Td>{new Date(inv.created_at).toLocaleDateString()}</Td>
                            <Td>
                              <HStack>
                                <Input size="xs" placeholder={t('payments.amount')||'Amount'} value={addPaymentState[inv.id]?.amount || ''} onChange={(e)=> setAddPaymentState(prev => ({ ...prev, [inv.id]: { ...(prev[inv.id]||{}), amount: e.target.value } }))} maxW="100px" />
                                <Input size="xs" type="date" value={addPaymentState[inv.id]?.payment_date || ''} onChange={(e)=> setAddPaymentState(prev => ({ ...prev, [inv.id]: { ...(prev[inv.id]||{}), payment_date: e.target.value } }))} maxW="140px" />
                                <Input size="xs" placeholder={t('payments.method')||'Method'} value={addPaymentState[inv.id]?.method || ''} onChange={(e)=> setAddPaymentState(prev => ({ ...prev, [inv.id]: { ...(prev[inv.id]||{}), method: e.target.value } }))} maxW="120px" />
                                <Button size="xs" onClick={async ()=>{
                                  try {
                                    const state = addPaymentState[inv.id] || {}; const amt = Number(state.amount || 0);
                                    if (!(amt>0)) { toast({ title: t('common.error'), description: 'Amount > 0', status:'error' }); return; }
                                    await api.post(`/api/invoices/${inv.id}/payments`, { amount: amt, payment_date: state.payment_date || null, method: state.method || null });
                                    toast({ title: t('common.success'), status:'success' });
                                    setAddPaymentState(prev => ({ ...prev, [inv.id]: {} }));
                                    refetchInvoiceSummary();
                                  } catch (e) {
                                    toast({ title: t('common.error'), description: e.response?.data?.error || 'Error', status:'error' });
                                  }
                                }}>{t('payments.add')||'Add'}</Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </CardBody>
                </Card>
              </VStack>
            ) : (
              <Text>{t('common.loading')||'Loading...'}</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={()=> setInvoiceModalOpen(false)}>{t('common.close')||'Close'}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Invoice from Proposal Modal */}
      <Modal isOpen={invoiceCreateOpen} onClose={() => setInvoiceCreateOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('invoices.new_from_proposal') || 'Create Invoice'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <HStack>
                <Select size="sm" value={invoiceCreateForm.invoice_type} onChange={(e)=> setInvoiceCreateForm(prev => ({ ...prev, invoice_type: e.target.value }))}>
                  <option value="advance">Advance</option>
                  <option value="completion">Completion</option>
                  <option value="extra">Extra</option>
                </Select>
                <Select size="sm" value={invoiceCreateForm.mode} onChange={(e)=> setInvoiceCreateForm(prev => ({ ...prev, mode: e.target.value }))} isDisabled={invoiceCreateForm.invoice_type==='extra'}>
                  <option value="percent">Percent</option>
                  <option value="fixed">Fixed</option>
                </Select>
                <Input size="sm" type="number" step="0.01" placeholder="Value" value={invoiceCreateForm.value} onChange={(e)=> setInvoiceCreateForm(prev => ({ ...prev, value: e.target.value }))} />
              </HStack>
              <HStack>
                <Input size="sm" type="date" value={invoiceCreateForm.due_date} onChange={(e)=> setInvoiceCreateForm(prev => ({ ...prev, due_date: e.target.value }))} />
                <Input size="sm" placeholder="Notes" value={invoiceCreateForm.notes} onChange={(e)=> setInvoiceCreateForm(prev => ({ ...prev, notes: e.target.value }))} />
              </HStack>
              {invoiceCreateForm.invoice_type === 'extra' && (
                <Box border="1px" borderColor="gray.200" p={3} borderRadius="md">
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="bold">Items</Text>
                    <Button size="xs" onClick={()=> setInvoiceCreateForm(prev => ({ ...prev, items: [...prev.items, { name:'', quantity:1, unit_price:0, total_price:0, is_extra:1 }] }))}>{t('common.add')||'Add'}</Button>
                  </HStack>
                  {(invoiceCreateForm.items || []).map((it, idx) => (
                    <HStack key={idx} mb={2}>
                      <Input size="sm" placeholder="Name" value={it.name} onChange={(e)=> setInvoiceCreateForm(prev=>{ const arr=[...prev.items]; arr[idx]={...arr[idx], name:e.target.value}; return {...prev, items:arr}; })} />
                      <Input size="sm" type="number" step="1" min="1" placeholder="Qty" value={it.quantity} onChange={(e)=> setInvoiceCreateForm(prev=>{ const arr=[...prev.items]; const v=Number(e.target.value); arr[idx]={...arr[idx], quantity:v, total_price:v*Number(arr[idx].unit_price||0)}; return {...prev, items:arr}; })} maxW="90px" />
                      <Input size="sm" type="number" step="0.01" placeholder="Unit Price" value={it.unit_price} onChange={(e)=> setInvoiceCreateForm(prev=>{ const arr=[...prev.items]; const v=Number(e.target.value); arr[idx]={...arr[idx], unit_price:v, total_price:v*Number(arr[idx].quantity||1)}; return {...prev, items:arr}; })} maxW="120px" />
                      <Text w="120px" textAlign="right" fontWeight="bold">{formatCurrency(it.total_price || 0)}</Text>
                    </HStack>
                  ))}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={()=> setInvoiceCreateOpen(false)}>{t('common.cancel')||'Cancel'}</Button>
              <Button colorScheme="green" onClick={async ()=>{
                try {
                  const payload = { proposal_id: Number(id), ...invoiceCreateForm };
                  await api.post('/api/invoices/create-from-proposal', payload);
                  toast({ title: t('common.success'), status:'success' });
                  setInvoiceCreateOpen(false);
                  setInvoiceCreateForm({ invoice_type: 'advance', mode:'percent', value:50, due_date:'', notes:'', items:[] });
                  refetchInvoiceSummary();
                } catch (e) {
                  toast({ title: t('common.error'), description: e.response?.data?.error || 'Error', status:'error' });
                }
              }}>{t('common.create')||'Create'}</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Context Menu for Row Options */}
      {rowContextMenu.open && (
        <>
          <Box
            position="fixed"
            top="0"
            left="0"
            right="0"
            bottom="0"
            zIndex={99998}
            onClick={() => setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null })}
          />
          <Box position="fixed" top={rowContextMenu.y} left={rowContextMenu.x} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" zIndex={99999} boxShadow="xl" minW="250px">
            <List spacing={0}>
              {(() => {
                const { sectionIndex, itemIndex } = rowContextMenu;
                const currentItem = sectionBlocks[sectionIndex]?.items[itemIndex];
                const isOptional = currentItem?.optional;
                const hidePrices = currentItem?.optional_hide_prices;

                const hasHidePriceNote = currentItem?.hide_price_note !== undefined && currentItem?.hide_price_note !== null && currentItem?.hide_price_note !== '';
                const hasProductNote = currentItem?.product_note !== undefined && currentItem?.product_note !== null && currentItem?.product_note !== '';

                return (
                  <>
                    {isOptional ? (
                      <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                        setSectionBlocks(prev => prev.map((b, i)=> i===sectionIndex ? {
                          ...b,
                          items: b.items.map((x,k)=> k===itemIndex ? { ...x, optional: false, optional_hide_prices: false } : x)
                        } : b));
                        setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                      }}>
                        <HStack>
                          <Text color="red.500">✕</Text>
                          <Text>{t('proposal.actions.remove_optional', 'Remove optional status')}</Text>
                        </HStack>
                      </ListItem>
                    ) : (
                      <>
                        <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                          setSectionBlocks(prev => prev.map((b, i)=> i===sectionIndex ? {
                            ...b,
                            items: b.items.map((x,k)=> k===itemIndex ? { ...x, optional: true, optional_hide_prices: false } : x)
                          } : b));
                          setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                        }}>
                          <Text>{t('proposal.actions.make_optional_with_prices', 'Make optional (with prices)')}</Text>
                        </ListItem>
                        <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                          setSectionBlocks(prev => prev.map((b, i)=> i===sectionIndex ? {
                            ...b,
                            items: b.items.map((x,k)=> k===itemIndex ? { ...x, optional: true, optional_hide_prices: true } : x)
                          } : b));
                          setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                        }}>
                          <Text>{t('proposal.actions.make_optional_no_prices', 'Make optional (without prices)')}</Text>
                        </ListItem>
                      </>
                    )}
                    <Divider />
                    <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                      setNoteModal({ isOpen: true, type: 'hide_price_note', sectionIndex, itemIndex, value: currentItem?.hide_price_note || '' });
                      setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                    }}>
                      <Text color={hasHidePriceNote ? 'red.500' : undefined}>{hasHidePriceNote ? '✓ ' : ''}{t('proposal.actions.hide_price_add_note', 'Hide Price & Add Note')}</Text>
                    </ListItem>
                    <ListItem p={3} cursor="pointer" _hover={{ bg: 'gray.100' }} onClick={() => {
                      setNoteModal({ isOpen: true, type: 'product_note', sectionIndex, itemIndex, value: currentItem?.product_note || '' });
                      setRowContextMenu({ open:false, x:0, y:0, sectionIndex:null, itemIndex:null });
                    }}>
                      <Text color={hasProductNote ? 'blue.500' : undefined}>{hasProductNote ? '✓ ' : ''}{t('proposal.actions.add_product_note', 'Add Product Note')}</Text>
                    </ListItem>
                  </>
                );
              })()}
            </List>
          </Box>
        </>
      )}
      {/* Note Modal */}
      <Modal isOpen={noteModal.isOpen} onClose={() => setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' })} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{noteModal.type === 'hide_price_note' ? t('proposal.actions.hide_price_add_note', 'Hide Price & Add Note') : t('proposal.actions.add_product_note', 'Add Product Note')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={noteModal.value}
              onChange={(e) => setNoteModal(prev => ({ ...prev, value: e.target.value }))}
              placeholder={noteModal.type === 'hide_price_note' ? t('proposal.actions.hide_price_note_prompt', 'Enter note (shown in red, price will be hidden)') : t('proposal.actions.product_note_prompt', 'Enter product note (shown bold below item)')}
              rows={3}
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <HStack>
              {noteModal.value && (
                <Button variant="ghost" colorScheme="red" onClick={() => {
                  const { sectionIndex, itemIndex, type } = noteModal;
                  setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                    ...b,
                    items: b.items.map((x, k) => k === itemIndex ? { ...x, [type]: null } : x)
                  }) : b));
                  setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' });
                }}>
                  {t('common.remove') || 'Remove'}
                </Button>
              )}
              <Button variant="ghost" onClick={() => setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' })}>{t('common.cancel') || 'Cancel'}</Button>
              <Button colorScheme="blue" onClick={() => {
                const { sectionIndex, itemIndex, type, value } = noteModal;
                setSectionBlocks(prev => prev.map((b, i) => i === sectionIndex ? ({
                  ...b,
                  items: b.items.map((x, k) => k === itemIndex ? { ...x, [type]: value || null } : x)
                }) : b));
                setNoteModal({ isOpen: false, type: null, sectionIndex: null, itemIndex: null, value: '' });
              }}>{t('common.save') || 'Save'}</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Item Modal */}
      <Modal isOpen={createItemModal.isOpen} onClose={() => setCreateItemModal({ isOpen: false, label: '', sectionIndex: null })} isCentered size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('inventory.create_item', { defaultValue: 'Create item' })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>{t('inventory.ask_add_stock_modal', { defaultValue: 'Do you want to add this to stock inventory or only to the proposal?' })}</Text>
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold">{createItemModal.label}</Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex w="100%" justify="flex-end" gap={3} flexWrap="wrap">
              <Button whiteSpace="nowrap" colorScheme="blue" onClick={async () => {
                try {
                  const name_en = createItemModal.label;
                  const name_de = createItemModal.label;
                  const res = await api.post('/api/inventory', {
                    name_en,
                    name_de,
                    description: '',
                    unit_price: 0,
                    stock_quantity: 0,
                    item_type: 'material',
                    uom: 'unit'
                  });
                  const createdId = res?.data?.data?.id || null;
                  const sIdx = createItemModal.sectionIndex;
                  setSectionBlocks(prev => prev.map((b, i) => i === sIdx ? {
                    ...b,
                    items: [
                      ...b.items,
                      {
                        item_id: createdId,
                        name: currentLanguage === 'de' ? name_de : name_en,
                        name_en,
                        name_de,
                        quantity: 1,
                        unit_price: 0,
                        uom_abbr: '',
                        item_type: 'product',
                        margin: null // Yeni ürün için margin boş
                      }
                    ]
                  } : b));
                  setCreateItemModal({ isOpen: false, label: '', sectionIndex: null });
                  toast({ title: t('common.success') || 'Success', status: 'success' });
                  setHasUnsavedChanges(true);
                } catch (e) {
                  toast({ title: t('common.error') || 'Error', description: t('messages.server_error') || 'Server error', status: 'error' });
                }
              }}>
                {t('inventory.add_to_db', { defaultValue: 'Add to database' })}
              </Button>
              <Button whiteSpace="nowrap" colorScheme="gray" onClick={() => {
                const name_en = createItemModal.label;
                const name_de = createItemModal.label;
                const sIdx = createItemModal.sectionIndex;
                setSectionBlocks(prev => prev.map((b, i) => i === sIdx ? {
                  ...b,
                  items: [
                    ...b.items,
                    {
                      item_id: null,
                      name: currentLanguage === 'de' ? name_de : name_en,
                      name_en,
                      name_de,
                      quantity: 1,
                      unit_price: 0,
                      uom_abbr: '',
                      item_type: 'product',
                      margin: null // Yeni ürün için margin boş
                    }
                  ]
                } : b));
                setCreateItemModal({ isOpen: false, label: '', sectionIndex: null });
                setHasUnsavedChanges(true);
              }}>
                {t('inventory.do_not_add_to_db', { defaultValue: 'Do not add to database' })}
              </Button>
              <Button whiteSpace="nowrap" variant="outline" onClick={() => setCreateItemModal({ isOpen: false, label: '', sectionIndex: null })}>
                {t('common.cancel') || 'Cancel'}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Exit Confirmation Dialog */}
      <Modal isOpen={showExitConfirm || pendingNavigation !== null} onClose={cancelExit} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('proposals.exit_confirmation_title', {defaultValue: 'Unsaved Changes'})}</ModalHeader>
          <ModalBody>
            <Text>{t('proposals.exit_confirmation_message', {defaultValue: 'You have unsaved changes. Do you want to save them before leaving?'})}</Text>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={cancelExit}>
                {t('common.cancel')}
              </Button>
              <Button variant="outline" onClick={confirmExit}>
                {t('proposals.discard_changes', {defaultValue: 'Discard Changes'})}
              </Button>
              <Button colorScheme="brand" onClick={() => {
                handleSave();
                clearUnsavedChanges(); // Clear navigation context
                confirmExit();
              }}>
                {t('proposals.save_and_exit', {defaultValue: 'Save & Exit'})}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProposalEdit;



